import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { addMinutes, format, parse } from 'date-fns';
import { usePaystackPayment } from 'react-paystack';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';
import {
  inferBookingFunnelReasonCode,
  trackBookingFunnelEvent,
} from '../utils/bookingFunnelTracking';

import {
  Banknote,
  ChevronLeft,
  CreditCard,
  Ticket,
  WalletCards,
} from 'lucide-react';

import UniAirLocationPicker from '../components/UniAirLocationPicker';
import AirportJourneyCard from './airport/AirportJourneyCard';
import { isWithinAirportSearchArea } from '../utils/airportLocationBounds';

/* -------------------------------------------------------
   Constants
-------------------------------------------------------- */
const PENDING_CARD_PAYMENT_KEY = "uniair_pending_card_payment";
const TOC_VERSION = '2026-02-13_v1';
const SUPPORT_EMAIL = 'support@uniair.co.za';
const SUPPORT_WHATSAPP = '27676656564';

// Booking-page typography: Manrope provides a cleaner, more premium look than the
// heavier display-style weights previously used. The fallback stack remains polished
// when the remote font cannot be loaded.
const UNIAIR_BOOKING_FONT_STYLESHEET_ID = 'uniair-booking-manrope-font';
const UNIAIR_BOOKING_FONT_STYLESHEET_URL =
  'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=optional';
const UNIAIR_BOOKING_FONT_FAMILY =
  "'Manrope', 'Avenir Next', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
const UNIAIR_BOOKING_TYPOGRAPHY = {
  fontFamily: UNIAIR_BOOKING_FONT_FAMILY,
  fontKerning: 'normal',
};

const TRAVEL_MINUTES = 45;
const BUFFER_MINUTES = 14;
const OP_BLOCK_MINUTES = TRAVEL_MINUTES + BUFFER_MINUTES;

const DROPOFF_SLOT_INTERVAL_MINUTES = 15;
const DROPOFF_SLOT_MINUTE_OPTIONS = ['00', '15', '30', '45'];
const DROPOFF_SLOT_HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0'));

const FLIGHT_TYPE_OPTIONS = [
  { value: 'domestic', label: 'Domestic' },
  { value: 'international', label: 'International' },
];

const AIRPORT_PICKUP_BUFFERS = {
  domestic: {
    no_luggage: 25,
    luggage: 40,
  },
  international: {
    no_luggage: 75,
    luggage: 90,
  },
};

const normalizeFlightType = (value) => {
  const normalized = String(value || '').toLowerCase().trim();
  return normalized === 'international' ? 'international' : 'domestic';
};

const getAirportPickupBufferMinutes = ({ tripMode, flightType, hasCheckInLuggage }) => {
  if (tripMode !== 'pickup') return 0;

  const safeFlightType = normalizeFlightType(flightType);
  const luggageKey = hasCheckInLuggage ? 'luggage' : 'no_luggage';

  return AIRPORT_PICKUP_BUFFERS[safeFlightType]?.[luggageKey] ?? 25;
};

const pad2 = (value) => String(value).padStart(2, '0');

const minutesToTimeString = (minutes) => {
  const normalized = ((Number(minutes) % 1440) + 1440) % 1440;
  const hours = Math.floor(normalized / 60);
  const mins = normalized % 60;
  return `${pad2(hours)}:${pad2(mins)}`;
};

const timeToMinutes = (timeValue) => {
  const normalized = String(timeValue || '').slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(normalized)) return null;

  const [hours, minutes] = normalized.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  return hours * 60 + minutes;
};

const getOptionFare = (option, fallback = 0) => {
  if (!option) return Math.max(0, moneyNumber(fallback));
  return Math.max(0, moneyNumber(option.price ?? fallback ?? 0));
};

const calculateActualPickupTime = ({
  tripMode,
  requestedTime,
  hasCheckInLuggage,
  flightType = 'domestic',
}) => {
  const safeTime = String(requestedTime || '').slice(0, 5);
  if (!safeTime) return '';

  if (tripMode !== 'pickup') return safeTime;

  try {
    const baseDate = parse(safeTime, 'HH:mm', new Date());

    const delay = getAirportPickupBufferMinutes({
      tripMode,
      flightType,
      hasCheckInLuggage,
    });

    return format(addMinutes(baseDate, delay), 'HH:mm');
  } catch {
    return safeTime;
  }
};

const roundTimeToNearestSlot = (timeValue, intervalMinutes = DROPOFF_SLOT_INTERVAL_MINUTES) => {
  const minutes = timeToMinutes(timeValue);
  if (minutes === null) return String(timeValue || '').slice(0, 5);

  const rounded = Math.round(minutes / intervalMinutes) * intervalMinutes;
  return minutesToTimeString(rounded);
};

const loadState = (key, defaultValue) => {
  try {
    const savedData = sessionStorage.getItem('uniair_booking_state');
    if (!savedData) return defaultValue;
    const parsed = JSON.parse(savedData);
    return parsed[key] !== undefined ? parsed[key] : defaultValue;
  } catch {
    return defaultValue;
  }
};

const normalizeFlightNumber = (value) => {
  if (!value) return '';
  return String(value).replace(/\s+/g, '').toUpperCase().trim();
};

const moneyNumber = (value, fallback = 0) => {
  const n = Number(value ?? fallback ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const moneyText = (value) => `R ${moneyNumber(value).toFixed(2)}`;


const buildImmediateRecoverySessionKey = (prefix, userId) => {
  const randomPart =
    typeof window !== 'undefined' &&
    window.crypto &&
    typeof window.crypto.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  return `${prefix}:${userId || 'anonymous'}:${randomPart}`.slice(0, 220);
};

const recoveryLocationPayload = (value, fallbackLabel = '') => {
  const location = normaliseBookingLocation(value);
  if (!location) {
    const label = String(fallbackLabel || '').trim();
    return label ? { label, formatted_address: label } : null;
  }

  return {
    label: location.label,
    formatted_address: location.formattedAddress || location.label,
    latitude: location.latitude,
    longitude: location.longitude,
    source: location.source,
  };
};

const recordImmediateNoAvailabilityRecovery = async ({
  marketId,
  productCode,
  userId,
  reasonCode,
  pageSource,
  journeyContext,
}) => {
  try {
    const { data, error } = await supabase.rpc(
      'record_immediate_no_availability_recovery_v1',
      {
        p_market_id: marketId || null,
        p_product_code: productCode,
        p_session_key: buildImmediateRecoverySessionKey(productCode, userId),
        p_reason_code: reasonCode,
        p_page_source: pageSource,
        p_journey_context: journeyContext || {},
      },
    );

    if (error) throw error;
    return data || null;
  } catch (error) {
    // Recovery analytics must never block the passenger booking flow.
    console.warn(
      '[Journey Recovery] Could not capture the no-availability trip intent:',
      error?.message || error,
    );
    return null;
  }
};

const resolveMyJourneyRecoveryAfterBooking = async ({
  productCode,
  entityType,
  entityId,
}) => {
  try {
    const { error } = await supabase.rpc(
      'resolve_my_journey_recovery_after_booking_v1',
      {
        p_product_code: productCode,
        p_entity_type: entityType || null,
        p_entity_id: entityId ? String(entityId) : null,
      },
    );

    if (error) throw error;
  } catch (error) {
    console.warn(
      '[Journey Recovery] Could not close the recovered journey case:',
      error?.message || error,
    );
  }
};

/* -------------------------------------------------------
   Coordinate-backed booking locations
   ------------------------------------------------------- */
const isResolvedBookingLocation = (value) => {
  const latitude = Number(value?.latitude);
  const longitude = Number(value?.longitude);

  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180 &&
    String(value?.label || '').trim().length > 0 &&
    value?.isSpecific !== false
  );
};

const normaliseBookingLocation = (value) => {
  if (!isResolvedBookingLocation(value)) return null;

  const rawSource = String(value.source || 'search').trim().toLowerCase();
  const source =
    rawSource === 'current_location' || rawSource.includes('device')
      ? 'current_location'
      : rawSource === 'map_pin' ||
          rawSource.includes('map') ||
          rawSource.includes('passenger_confirmed')
        ? 'map_pin'
        : 'search';

  return {
    label: String(value.label || '').trim(),
    formattedAddress: String(
      value.formattedAddress || value.formatted_address || value.label || '',
    ).trim(),
    latitude: Number(Number(value.latitude).toFixed(6)),
    longitude: Number(Number(value.longitude).toFixed(6)),
    provider: String(value.provider || 'geoapify').trim().toLowerCase(),
    providerFeatureId: String(
      value.providerFeatureId ||
        value.provider_feature_id ||
        value.placeId ||
        value.place_id ||
        '',
    ).trim(),
    source,
    resultType: String(value.resultType || value.result_type || '').trim(),
    displayType: String(value.displayType || value.display_type || '').trim(),
    isSpecific: true,
    originalLabel: String(
      value.originalLabel || value.original_label || value.label || '',
    ).trim(),
  };
};

const getBookingLocationText = (value, fallback = '') => {
  const location = normaliseBookingLocation(value);
  return location?.label || location?.formattedAddress || String(fallback || '').trim();
};

const buildStructuredLocationPayload = (value) => {
  const location = normaliseBookingLocation(value);
  if (!location) return null;

  return {
    label: location.label,
    formattedAddress: location.formattedAddress,
    latitude: location.latitude,
    longitude: location.longitude,
    provider: location.provider || 'geoapify',
    providerFeatureId: location.providerFeatureId || null,
    source: location.source,
  };
};

const formatConfirmationDate = (dateValue) => {
  if (!dateValue) return 'Scheduled date';

  try {
    const parsed = new Date(`${dateValue}T12:00:00`);
    if (Number.isNaN(parsed.getTime())) return String(dateValue);

    const today = new Date();
    if (parsed.toDateString() === today.toDateString()) return 'Today';

    return parsed.toLocaleDateString('en-ZA', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return String(dateValue);
  }
};

const cleanVehiclePart = (value) => String(value || '').trim();

const buildVehicleLabel = (row) => {
  const color = cleanVehiclePart(row?.car_color);
  const make = cleanVehiclePart(row?.car_make);
  const model = cleanVehiclePart(row?.car_model);

  const main = [color, make, model].filter(Boolean).join(' ').trim();
  if (main) return main;
  return 'Vehicle details pending';
};

const getDriverStudyLabel = (row) => {
  const study =
    row?.degree_program ||
    row?.driver_degree_program ||
    row?.study_program ||
    row?.programme ||
    row?.qualification ||
    '';

  return String(study || '').trim();
};

const getDriverUniversityLabel = (row) => {
  const university =
    row?.university_name ||
    row?.driver_university_name ||
    row?.university ||
    '';

  return String(university || '').trim();
};

const getDriverOperatingMarketLabel = (row) => {
  const market =
    row?.operating_market_name ||
    row?.driver_operating_market_name ||
    '';

  return String(market || '').trim();
};

const WhatsAppIcon = ({ className = 'w-5 h-5 fill-current' }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const driverVehiclePriority = (row) => {
  const ownershipType = String(row?.ownership_type || '').toLowerCase();
  const operatingModel = String(row?.operating_model || row?.vehicle_operating_model || '').toLowerCase();
  const assignmentType = String(row?.assignment_type || '').toLowerCase();
  const assignmentState = String(row?.assignment_state || '').toLowerCase();
  const vehicleSource = String(row?.vehicle_source || row?.vehicle_context || row?.source || '').toLowerCase();

  const hasAssignmentId = !!(
    row?.vehicle_assignment_id ||
    row?.assignment_id ||
    row?.active_assignment_id ||
    row?.current_assignment_id
  );

  const looksAssigned =
    hasAssignmentId ||
    assignmentType === 'primary' ||
    assignmentType === 'temporary' ||
    assignmentType === 'backup' ||
    assignmentState === 'active' ||
    vehicleSource.includes('assign') ||
    vehicleSource.includes('company') ||
    operatingModel === 'company_vehicle' ||
    ownershipType === 'company_vehicle' ||
    row?.managed_by_company === true;

  if (looksAssigned) return 1;
  if (ownershipType === 'owner_driver') return 3;

  return 4;
};

const dedupeAvailableDrivers = (rows = []) => {
  const byDriver = new Map();

  rows.forEach((row) => {
    if (!row?.driver_id) return;

    const existing = byDriver.get(row.driver_id);
    if (!existing) {
      byDriver.set(row.driver_id, row);
      return;
    }

    const existingPriority = driverVehiclePriority(existing);
    const currentPriority = driverVehiclePriority(row);

    if (currentPriority < existingPriority) {
      byDriver.set(row.driver_id, row);
      return;
    }

    const existingCompleteness = [
      existing.vehicle_id,
      existing.license_plate,
      existing.car_make,
      existing.car_model,
      existing.car_color,
    ].filter(Boolean).length;

    const currentCompleteness = [
      row.vehicle_id,
      row.license_plate,
      row.car_make,
      row.car_model,
      row.car_color,
    ].filter(Boolean).length;

    if (currentPriority === existingPriority && currentCompleteness > existingCompleteness) {
      byDriver.set(row.driver_id, row);
    }
  });

  return Array.from(byDriver.values());
};

export default function ClientBooking() {
  const {
    user,
    requiresStudentReverification,
    canBookTrips,
    studentVerificationExpiresAtDate,
    studentVerificationDaysRemaining,
  } = useAuth();

  const navigate = useNavigate();
  const analyticsPageOpenedRef = useRef(false);

  const trackAirportBookingEvent = (eventName, reasonCode = null, bookingId = null) =>
    trackBookingFunnelEvent(eventName, {
      reasonCode,
      rideMode: 'airport_transfer',
      pageSource: 'client_booking',
      marketId: user?.market_id || null,
      bookingId,
    });

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const existing = document.getElementById(UNIAIR_BOOKING_FONT_STYLESHEET_ID);
    if (existing) return undefined;

    const link = document.createElement('link');
    link.id = UNIAIR_BOOKING_FONT_STYLESHEET_ID;
    link.rel = 'stylesheet';
    link.href = UNIAIR_BOOKING_FONT_STYLESHEET_URL;
    document.head.appendChild(link);

    return undefined;
  }, []);


  useEffect(() => {
    if (!user?.id || analyticsPageOpenedRef.current) return;

    analyticsPageOpenedRef.current = true;
    void trackAirportBookingEvent('booking_page_opened');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.market_id]);

  /* -------------------------------------------------------
     UI / Flow State
  -------------------------------------------------------- */
  const [step, setStep] = useState(() => loadState('step', 1));
  const [loading, setLoading] = useState(false);
  const [availableOptions, setAvailableOptions] = useState(() => loadState('availableOptions', []));
  const [notification, setNotification] = useState(null);
  const [alternativeTimes, setAlternativeTimes] = useState(() => loadState('alternativeTimes', []));
  const [searchedTimeSummary, setSearchedTimeSummary] = useState(() => loadState('searchedTimeSummary', null));
  const [agreedToTerms, setAgreedToTerms] = useState(() => loadState('agreedToTerms', false));

  const [isStudent, setIsStudent] = useState(() => {
    const studentEmail = String(user?.student_email || '').toLowerCase();
    const email = String(user?.email || '').toLowerCase();
    return studentEmail.endsWith('.ac.za') || email.endsWith('.ac.za');
  });

  /* -------------------------------------------------------
     Market / Zone / Route setup
  -------------------------------------------------------- */
  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [routeProducts, setRouteProducts] = useState([]);
  const [zoneId, setZoneId] = useState(() => loadState('zoneId', ''));
  const [marketLaunchContext, setMarketLaunchContext] = useState(null);
  const [marketLaunchLoading, setMarketLaunchLoading] = useState(false);
  const [airportProductEnabled, setAirportProductEnabled] = useState(true);

  useEffect(() => {
    if (!user?.id || !user?.market_id) return undefined;
    let active = true;
    const refresh = async () => {
      const { data, error } = await supabase.rpc('uniair_get_my_product_status_v1');
      if (active && !error && data?.ok) setAirportProductEnabled(data.airport !== false);
    };
    void refresh();
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { active = false; document.removeEventListener('visibilitychange', onVisible); };
  }, [user?.id, user?.market_id]);

  // A passenger keeps their own university market while an approved external
  // Airport Transfer pickup area controls the real collection geography.
  const [departureContext, setDepartureContext] = useState(null);
  const [departureContextLoading, setDepartureContextLoading] = useState(false);

  const selectedZone = useMemo(() => zones.find((z) => z.zone_id === zoneId) || null, [zones, zoneId]);
  const zoneName = selectedZone?.name || '';

  const marketCanBook = marketLaunchContext?.can_book === true && airportProductEnabled;
  const marketBookingMessage =
    (!airportProductEnabled ? 'Airport Transfers are temporarily unavailable for this market.' : null) ||
    marketLaunchContext?.booking_closed_message ||
    marketLaunchContext?.launch_message ||
    'Bookings are not open for your university yet.';

  /* -------------------------------------------------------
     Wallet / Promo / Booking Form State
  -------------------------------------------------------- */
  const [walletBalance, setWalletBalance] = useState(0);
  const [debtAmount, setDebtAmount] = useState(0);
  const [useWallet, setUseWallet] = useState(true);

  const [promoCode, setPromoCode] = useState(() => loadState('promoCode', ''));
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMsg, setPromoMsg] = useState(null);

  // ✅ Promo preview only. This does NOT create a booking.
  const [appliedPromo, setAppliedPromo] = useState(() => loadState('appliedPromo', null));

const [draftBookingId, setDraftBookingId] = useState(null);
const [draftBooking, setDraftBooking] = useState(null);

  const [tripType, setTripType] = useState(() => loadState('tripType', 'dropoff'));
  const [date, setDate] = useState(() => loadState('date', format(new Date(), 'yyyy-MM-dd')));
  const [time, setTime] = useState(() => loadState('time', '10:00'));
  const [hasLuggage, setHasLuggage] = useState(() => loadState('hasLuggage', true));
  const [flightType, setFlightType] = useState(() => loadState('flightType', 'domestic'));
  const [flightNumber, setFlightNumber] = useState(() => loadState('flightNumber', ''));
  const [location, setLocation] = useState(() => loadState('location', ''));
  const [preciseLocation, setPreciseLocation] = useState(() => loadState('preciseLocation', null));
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const airportSearchAreaRef = useRef({ marketId: null, area: null });
  const [paymentMethod, setPaymentMethod] = useState(() => loadState('paymentMethod', 'cash'));
  const [saveCardForFuture, setSaveCardForFuture] = useState(false);
  const [airportSavedCards, setAirportSavedCards] = useState([]);
  const [airportCardSource, setAirportCardSource] = useState('new');
  const [airportSelectedCardId, setAirportSelectedCardId] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    supabase.rpc('uniair_rapid_my_saved_cards_v1')
      .then(({data,error}) => {
        if (!active || error || !Array.isArray(data)) return;
        setAirportSavedCards(data);
      }).catch(() => {});
    return () => { active = false; };
  }, [user?.id]);
  const [selectedOption, setSelectedOption] = useState(() => loadState('selectedOption', null));
  const [calculatedTime, setCalculatedTime] = useState(() => loadState('calculatedTime', ''));
  const [basePrice, setBasePrice] = useState(0);

  const hasPreciseLocation = useMemo(
    () => isResolvedBookingLocation(preciseLocation),
    [preciseLocation],
  );

  const isExternalDeparture =
    tripType === 'dropoff' && departureContext?.is_external_departure === true;

  const isExternalDepartureReady =
    isExternalDeparture &&
    departureContext?.is_active === true &&
    departureContext?.booking_market_open === true &&
    !!departureContext?.pricing_zone_id;

  const bookingZoneId = isExternalDeparture
    ? departureContext?.pricing_zone_id || null
    : zoneId;

  const bookingZoneName = isExternalDeparture
    ? departureContext?.label || departureContext?.operating_area_name || 'Cape Town'
    : zoneName;

  const selectedDropoffSlotTime = useMemo(
    () => roundTimeToNearestSlot(time || '10:00', DROPOFF_SLOT_INTERVAL_MINUTES),
    [time]
  );

  const dropoffSelectedHour = selectedDropoffSlotTime.slice(0, 2);
  const dropoffSelectedMinute = selectedDropoffSlotTime.slice(3, 5);

  const activeRouteProduct = useMemo(() => {
    return routeProducts.find((rp) => rp.trip_type === tripType && rp.is_active) || null;
  }, [routeProducts, tripType]);

  const requiresFlightNumber = !!activeRouteProduct?.requires_flight_number;

  const actualPickupTimeForSelectedInputs = useMemo(
    () =>
      calculateActualPickupTime({
        tripMode: tripType,
        requestedTime: time,
        hasCheckInLuggage: hasLuggage,
        flightType,
      }),
    [tripType, time, hasLuggage, flightType]
  );

  const airportBufferMinutesForSelectedInputs = useMemo(
    () =>
      getAirportPickupBufferMinutes({
        tripMode: tripType,
        flightType,
        hasCheckInLuggage: hasLuggage,
      }),
    [tripType, flightType, hasLuggage]
  );

  /* -------------------------------------------------------
     Paystack State
  -------------------------------------------------------- */
  const [paystackAmount, setPaystackAmount] = useState(null);
  const [paystackReference, setPaystackReference] = useState(null);
  const [pendingPaystackLaunch, setPendingPaystackLaunch] = useState(null);
  const [paymentUiStage, setPaymentUiStage] = useState('idle');
  const [paymentCloseNotice, setPaymentCloseNotice] = useState(false);

  const paystackBookingIdRef = useRef(null);
  const walletAppliedRef = useRef(0);
  const paystackReferenceRef = useRef(null);
  const paystackOpeningRef = useRef(false);

  const verificationExpiryLabel = useMemo(() => {
    if (!studentVerificationExpiresAtDate) return '';
    try {
      return studentVerificationExpiresAtDate.toLocaleDateString('en-ZA', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  }, [studentVerificationExpiresAtDate]);

  /* -------------------------------------------------------
     Auto-save state
  -------------------------------------------------------- */
  useEffect(() => {
    const stateToSave = {
    step,
    tripType,
    date,
    time,
    hasLuggage,
    flightType,
    flightNumber,
    location,
    preciseLocation,
    zoneId,
    paymentMethod,
    selectedOption,
    calculatedTime,
    availableOptions,
    alternativeTimes,
    searchedTimeSummary,
    agreedToTerms,
    promoCode,
    appliedPromo,
  };

    sessionStorage.setItem('uniair_booking_state', JSON.stringify(stateToSave));
  }, [
    step,
    tripType,
    date,
    time,
    hasLuggage,
    flightType,
    flightNumber,
    location,
    preciseLocation,
    zoneId,
    paymentMethod,
    selectedOption,
    calculatedTime,
    availableOptions,
    alternativeTimes,
    searchedTimeSummary,
    agreedToTerms,
    promoCode,
    appliedPromo,
  ]);

  useEffect(() => {
    if (tripType === 'dropoff') {
      setFlightNumber('');
      setFlightType('domestic');
    }
  }, [tripType]);

  useEffect(() => {
    if (tripType !== 'dropoff') return;

    const normalizedTime = roundTimeToNearestSlot(time, DROPOFF_SLOT_INTERVAL_MINUTES);
    if (normalizedTime && normalizedTime !== time) {
      setTime(normalizedTime);
    }
  }, [tripType, time]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    window.setTimeout(() => setNotification(null), 5000);
  };

  const handleTripTypeChange = (nextTripType) => {
    if (nextTripType === tripType) return;

    void trackAirportBookingEvent('trip_type_selected');
    setTripType(nextTripType);
    setLocation('');
    setPreciseLocation(null);
    setDepartureContext(null);
    setCalculatedTime('');
  };

  const handleOpenLocationPicker = () => {
    void trackAirportBookingEvent(
      tripType === 'dropoff'
        ? 'pickup_location_started'
        : 'dropoff_location_started',
    );
    setLocationPickerOpen(true);
  };

  // Both inline Google Places and the full-screen map feed the same precise
  // location payload used by airport availability, routing and booking RPCs.
  const resetLocationDependentSearch = () => {
    setDepartureContext(null);
    setSelectedOption(null);
    setAvailableOptions([]);
    setAlternativeTimes([]);
    setSearchedTimeSummary(null);
    setCalculatedTime('');
    // Any earlier draft/quote belongs to the previously selected location.
    clearDraftState();
  };

  const validateInlineAirportLocation = async (point) => {
    const marketId = user?.market_id || null;
    if (!marketId) throw new Error('Your booking area could not be verified. Please try the map pin.');

    let area = airportSearchAreaRef.current.marketId === marketId
      ? airportSearchAreaRef.current.area
      : null;

    if (!area) {
      // The same context API used by the full-screen location picker.
      const { data, error } = await supabase.functions.invoke('uniair-location-search', {
        body: {
          action: 'context',
          search_scope: tripType === 'dropoff' ? 'all' : 'market',
        },
      });
      area = data?.search_area || data?.context?.serviceArea || data?.context?.service_area;
      if (error || !data?.ok || !area) {
        throw new Error('Your booking area is unavailable. Please try the map pin.');
      }
      airportSearchAreaRef.current = { marketId, area };
    }

    if (!isWithinAirportSearchArea(point, area)) {
      throw new Error(`Choose a location inside ${area.label || 'your UniAir booking area'}, or adjust the map pin.`);
    }

    handleLocationSelected(point);
  };

  const handleLocationTextChange = (nextValue) => {
    setLocation(nextValue);
    setPreciseLocation(null);
    resetLocationDependentSearch();
  };

  const handleLocationSelected = (point) => {
    const normalizedPoint = normaliseBookingLocation(point);
    if (!normalizedPoint) return;
    setPreciseLocation(normalizedPoint);
    setLocation(getBookingLocationText(normalizedPoint));
    resetLocationDependentSearch();
    void trackAirportBookingEvent(
      tripType === 'dropoff'
        ? 'pickup_location_selected'
        : 'dropoff_location_selected',
    );
  };

  const handleTravelDateChange = (nextDate) => {
    setDate(nextDate);
    void trackAirportBookingEvent('travel_date_selected');
  };

  const trackAirportAvailabilityShown = () => {
    void trackAirportBookingEvent('availability_found');
    void trackAirportBookingEvent('fare_shown');
  };

  /* -------------------------------------------------------
     Market launch / booking access
  -------------------------------------------------------- */
  useEffect(() => {
    let cancelled = false;

    const loadMarketLaunchContext = async () => {
      if (!user?.market_id) {
        setMarketLaunchContext(null);
        setZones([]);
        setRouteProducts([]);
        return;
      }

      try {
        setMarketLaunchLoading(true);

        const { data, error } = await supabase.rpc('get_market_launch_context_v1', {
          p_university_id: user?.university_id || null,
          p_campus_id: user?.campus_id || null,
          p_market_id: user.market_id,
        });

        if (error) throw error;

        if (!cancelled) {
          setMarketLaunchContext(data || null);

          if (data?.can_book !== true) {
            setZones([]);
            setRouteProducts([]);
            setAvailableOptions([]);
            setAlternativeTimes([]);
            setSelectedOption(null);
          }
        }
      } catch (err) {
        console.error('Error loading market launch context:', err);
        if (!cancelled) {
          setMarketLaunchContext(null);
          setZones([]);
          setRouteProducts([]);
          showNotification('error', 'Could not check booking availability for your university.');
        }
      } finally {
        if (!cancelled) setMarketLaunchLoading(false);
      }
    };

    loadMarketLaunchContext();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.market_id, user?.university_id, user?.campus_id]);

  /* -------------------------------------------------------
     Approved external Airport Transfer departure area
  -------------------------------------------------------- */
  useEffect(() => {
    let cancelled = false;

    const resolveDepartureArea = async () => {
      if (
        tripType !== 'dropoff' ||
        !user?.market_id ||
        !isResolvedBookingLocation(preciseLocation)
      ) {
        setDepartureContext(null);
        setDepartureContextLoading(false);
        return;
      }

      try {
        setDepartureContextLoading(true);

        const point = normaliseBookingLocation(preciseLocation);
        const { data, error } = await supabase.rpc(
          'resolve_airport_transfer_departure_context_v1',
          {
            p_booking_market_id: user.market_id,
            p_pickup_latitude: point?.latitude ?? null,
            p_pickup_longitude: point?.longitude ?? null,
          },
        );

        if (error) throw error;
        if (!cancelled) setDepartureContext(data || null);
      } catch (error) {
        console.error('Could not check airport departure area:', error);
        if (!cancelled) {
          setDepartureContext(null);
          showNotification(
            'error',
            'Could not check whether this pickup area is available for Airport Transfers.',
          );
        }
      } finally {
        if (!cancelled) setDepartureContextLoading(false);
      }
    };

    void resolveDepartureArea();

    return () => {
      cancelled = true;
    };
  }, [tripType, user?.market_id, preciseLocation]);

  /* -------------------------------------------------------
     Load setup
  -------------------------------------------------------- */
  useEffect(() => {
    const loadBookingSetup = async () => {
      if (!user?.market_id || !marketCanBook) {
        setZones([]);
        setRouteProducts([]);
        return;
      }

      try {
        setZonesLoading(true);

        const [{ data: zoneRows, error: zoneErr }, { data: routeRows, error: routeErr }] =
          await Promise.all([
            supabase
              .from('service_zones')
              .select('*')
              .eq('market_id', user.market_id)
              .eq('is_active', true)
              .order('display_order', { ascending: true }),
            supabase
              .from('route_products')
              .select('*')
              .eq('market_id', user.market_id)
              .eq('is_active', true),
          ]);

        if (zoneErr) throw zoneErr;
        if (routeErr) throw routeErr;

        const nextZones = zoneRows || [];
        setZones(nextZones);
        setRouteProducts(routeRows || []);

        if (!zoneId && nextZones.length > 0) {
          setZoneId(nextZones[0].zone_id);
        } else if (zoneId && !nextZones.some((z) => z.zone_id === zoneId) && nextZones.length > 0) {
          setZoneId(nextZones[0].zone_id);
        }
      } catch (err) {
        console.error('Error loading booking setup:', err);
        showNotification('error', 'Could not load booking setup.');
      } finally {
        setZonesLoading(false);
      }
    };

    if (canBookTrips && marketCanBook) loadBookingSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.market_id, zoneId, canBookTrips, marketCanBook]);

  /* -------------------------------------------------------
     Live pricing / helpers
  -------------------------------------------------------- */
  const getLiveBasePrice = async ({ tripMode, rideMode, reqZoneId, driverId = null }) => {
    if (!user?.market_id) throw new Error('User market is missing.');
    if (!marketCanBook) throw new Error(marketBookingMessage);

    const { data, error } = await supabase.rpc('resolve_booking_quote_v1', {
      p_market_id: user.market_id,
      p_trip_type: tripMode,
      p_ride_mode: rideMode,
      p_zone_id: tripMode === 'dropoff' ? reqZoneId || null : null,
      p_driver_id: driverId,
      p_seats_basis: null,
    });

    if (error) throw error;

    const liveBasePrice = moneyNumber(data?.pricing?.base_price);
    if (!Number.isFinite(liveBasePrice) || liveBasePrice <= 0) {
      throw new Error('Could not resolve live pricing.');
    }

    return { quote: data, basePrice: liveBasePrice };
  };

  const buildWhatsAppLink = () => {
    const searchTime = tripType === 'pickup' ? calculatedTime || time : time;
    const msg = `Hi UniAir support, I tried booking a ${
      tripType === 'pickup' ? 'pickup from the airport' : 'dropoff to the airport'
    } on ${date} at ${searchTime}, but no drivers were available. Could you please check with drivers directly? My pickup/dropoff area is ${location}${
      tripType === 'dropoff' && bookingZoneName ? ` (${bookingZoneName})` : ''
    }.`;

    return `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(msg)}`;
  };

  const fetchAlternativeTimes = async ({
    reqDate,
    reqTime,
    computedPickupTime,
    tripMode,
    reqZone,
    pickupLocation,
  }) => {
    const safeTime = (tripMode === 'pickup' ? computedPickupTime : reqTime)?.slice(0, 5) || '12:00';
    const point = normaliseBookingLocation(pickupLocation);

    const { data, error } = await supabase.rpc('get_next_available_airport_driver_times_v3', {
      p_search_date: reqDate,
      p_search_time: safeTime,
      p_trip_mode: tripMode,
      p_service_market_id: user?.market_id || null,
      p_zone_id: tripMode === 'dropoff' ? reqZone || null : null,
      p_pickup_latitude: tripMode === 'dropoff' ? point?.latitude ?? null : null,
      p_pickup_longitude: tripMode === 'dropoff' ? point?.longitude ?? null : null,
      p_slots_before: 4,
      p_slots_after: 4,
      p_slot_interval_minutes: tripMode === 'dropoff' ? DROPOFF_SLOT_INTERVAL_MINUTES : 30,
    });

    if (error) throw error;
    return data || [];
  };

  const attachDriverDisplayIdentities = async (options = []) => {
    const driverIds = Array.from(
      new Set(
        options
          .map((option) => option?.driver_id)
          .filter(Boolean),
      ),
    );

    if (driverIds.length === 0) return options;

    const needsIdentity = options.some(
      (option) => !getDriverStudyLabel(option) || !getDriverUniversityLabel(option),
    );
    if (!needsIdentity) return options;

    try {
      const { data, error } = await supabase.rpc(
        'get_driver_display_identities_v1',
        { p_driver_ids: driverIds },
      );

      if (error) throw error;

      const identityByDriverId = new Map(
        (data || []).map((row) => [row.driver_id, row]),
      );

      return options.map((option) => {
        const identity = identityByDriverId.get(option.driver_id);
        if (!identity) return option;

        return {
          ...option,
          degree_program: getDriverStudyLabel(option) || identity.degree_program || null,
          university_name: getDriverUniversityLabel(option) || identity.university_name || null,
          university_short_code:
            option.university_short_code || identity.university_short_code || null,
          home_market_id: option.home_market_id || identity.home_market_id || null,
          home_market_name: option.home_market_name || identity.home_market_name || null,
          operating_market_id:
            option.operating_market_id || identity.operating_market_id || null,
          operating_market_name:
            option.operating_market_name || identity.operating_market_name || null,
        };
      });
    } catch (err) {
      console.warn('Could not hydrate driver display identities:', err?.message || err);
      return options;
    }
  };

  const retryWithSuggestedTime = async (newTime) => {
    if (!canBookTrips) {
      showNotification('error', 'Annual student verification required before booking.');
      navigate('/profile');
      return;
    }

    const normalizedTime =
      tripType === 'dropoff'
        ? roundTimeToNearestSlot(newTime, DROPOFF_SLOT_INTERVAL_MINUTES)
        : newTime;

    setTime(normalizedTime);

    const finalTime = calculateActualPickupTime({
      tripMode: tripType,
      requestedTime: normalizedTime,
      hasCheckInLuggage: hasLuggage,
      flightType,
    });

    setCalculatedTime(finalTime);

    await fetchOptions({
      reqDate: date,
      reqTime: normalizedTime,
      computedPickupTime: finalTime,
      tripMode: tripType,
      reqZoneId: bookingZoneId,
      reqZoneName: bookingZoneName,
      pickupLocation: preciseLocation,
    });
  };

  const joinSharedRide = (payload) =>
    supabase.rpc(
      selectedOption?.airport_departure_area_id
        ? 'instant_join_airport_departure_shared_ride_v1'
        : 'instant_join_shared_ride',
      payload,
    );

  const buildDriverAndVehiclePayload = () => {
    const driverData = {
      profiles: {
        first_name: selectedOption?.name || selectedOption?.first_name || '',
        last_name: selectedOption?.last_name || '',
        profile_photo_url: selectedOption?.photo || selectedOption?.driver_profile_photo_url || null,
        phone_number: selectedOption?.phone_number || null,
      },
    };

    const hasStructuredVehicle =
      selectedOption?.car_make || selectedOption?.car_model || selectedOption?.car_color || selectedOption?.license_plate;

    const vehicleData = hasStructuredVehicle
      ? {
          color: selectedOption?.car_color || '',
          make: selectedOption?.car_make || '',
          model: selectedOption?.car_model || '',
          license_plate: selectedOption?.license_plate || '',
          vehicle_photo_url: selectedOption?.vehicle_photo_url || null,
          seats_available: selectedOption?.seats_available ?? null,
        }
      : (() => {
          const carParts = selectedOption?.car ? String(selectedOption.car).split(' ') : [];
          return {
            color: carParts[0] || '',
            make: carParts[1] || '',
            model: carParts.slice(2).join(' ') || '',
          };
        })();

    return { driverData, vehicleData };
  };

  const goToBookingSuccess = ({ booking_id, price, method, dateStr }) => {
    sessionStorage.removeItem('uniair_booking_state');
    const { driverData, vehicleData } = buildDriverAndVehiclePayload();

    void trackAirportBookingEvent('booking_created', null, booking_id);
    void resolveMyJourneyRecoveryAfterBooking({
      productCode: 'airport_transfers',
      entityType: 'booking',
      entityId: booking_id,
    });

    navigate('/booking-success', {
      state: {
        booking_id,
        driver: driverData,
        time: tripType === 'pickup' ? calculatedTime : time,
        price,
        method,
        date: dateStr,
        vehicle: vehicleData,
      },
    });
  };

  /* -------------------------------------------------------
     Wallet/Profile
  -------------------------------------------------------- */
  useEffect(() => {
    if (user?.id) fetchWalletAndProfileFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchWalletAndProfileFlags = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('account_credit, student_number, student_email')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      const balance = moneyNumber(data?.account_credit);
      setWalletBalance(balance);
      setDebtAmount(balance < 0 ? Math.abs(balance) : 0);

      const studentNo = (data?.student_number ?? '').toString().trim();
      const studentEmail = String(data?.student_email || '').toLowerCase();
      const email = String(user?.email || '').toLowerCase();

      setIsStudent(!!studentNo || studentEmail.endsWith('.ac.za') || email.endsWith('.ac.za'));
    } catch (err) {
      console.error('Error fetching wallet/profile:', err);
    }
  };

  useEffect(() => {
    if (!user?.id) return;

    const ch = supabase
      .channel(`rt-profiles-booking-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'profiles', filter: `id=eq.${user.id}` },
        (payload) => {
          const row = payload.new || payload.old;
          if (!row) return;

          const balance = moneyNumber(row.account_credit);
          setWalletBalance(balance);
          setDebtAmount(balance < 0 ? Math.abs(balance) : 0);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(ch);
    };
  }, [user?.id]);

  /* -------------------------------------------------------
     Derived Pricing
  -------------------------------------------------------- */
  const walletAvailable = useMemo(() => Math.max(0, moneyNumber(walletBalance)), [walletBalance]);

  const effectiveFare = useMemo(() => {
    if (!selectedOption) return 0;

    const expectedFare = getOptionFare(selectedOption, basePrice);

    // Promo preview is already calculated from the selected option fare.
    if (appliedPromo && !draftBooking) {
      return moneyNumber(appliedPromo.final_price ?? expectedFare);
    }

    if (draftBooking) {
      return moneyNumber(draftBooking.final_price ?? draftBooking.price ?? expectedFare);
    }

    return expectedFare;
  }, [selectedOption, draftBooking, appliedPromo, basePrice]);

  const totalBeforeWallet = useMemo(() => {
    if (!selectedOption) return 0;
    return Math.max(0, effectiveFare + moneyNumber(debtAmount));
  }, [selectedOption, debtAmount, effectiveFare]);

  const creditToApply = useMemo(() => {
    if (!selectedOption) return 0;
    if (!useWallet) return 0;
    if (walletAvailable <= 0) return 0;
    return Math.min(walletAvailable, totalBeforeWallet);
  }, [selectedOption, useWallet, walletAvailable, totalBeforeWallet]);

  const amountToPayNow = useMemo(
    () => Math.max(0, totalBeforeWallet - creditToApply),
    [totalBeforeWallet, creditToApply]
  );

  const selectedBaseFare = useMemo(() => {
    if (!selectedOption) return 0;
    return getOptionFare(selectedOption, basePrice);
  }, [selectedOption, basePrice]);

  const displayedDiscountAmount = useMemo(
    () => moneyNumber(draftBooking?.discount_amount ?? appliedPromo?.discount_amount ?? 0),
    [draftBooking, appliedPromo]
  );

  const confirmationDriverName = `${selectedOption?.name || ''} ${selectedOption?.last_name || ''}`.trim() || 'UniAir Driver';
  const confirmationRideType = `${String(selectedOption?.type || 'private').replace(/^./, (letter) => letter.toUpperCase())} ride`;
  const confirmationRouteLabel =
    tripType === 'dropoff'
      ? `${location || zoneName || 'Your pickup location'} → Airport`
      : `Airport → ${location || 'Your destination'}`;
  const confirmationDateLabel = formatConfirmationDate(date);
  const confirmationMainTime = tripType === 'pickup' ? calculatedTime || actualPickupTimeForSelectedInputs || time : time;

  /* -------------------------------------------------------
     Paystack Config + Helpers
  -------------------------------------------------------- */
  const paystackEmail = user?.email || 'support@uniair.com';

  const paystackOriginalBookingId =
    selectedOption?.type === 'shared' && Number(selectedOption?.delay) <= 5
      ? selectedOption?.booking_id || null
      : null;

  const paystackConfig = useMemo(() => {
    const ref =
      paystackReference || paystackReferenceRef.current || `MS_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;

    const cents = Math.max(0, Math.round(moneyNumber(paystackAmount ?? amountToPayNow) * 100));
    const bookingId = draftBookingId || paystackBookingIdRef.current || null;

    return {
      reference: ref,
      email: paystackEmail,
      amount: cents,
      publicKey:
        process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_live_dd59be131e1a5b5c9ae0ee8a40bf628292fcc757',
      currency: 'ZAR',
      metadata: {
        user_id: user?.id,
        booking_id: bookingId,
        original_booking_id: paystackOriginalBookingId,
        purpose: 'booking_payment',
        custom_fields: [
          { display_name: 'Purpose', variable_name: 'purpose', value: 'booking_payment' },
          { display_name: 'User ID', variable_name: 'user_id', value: user?.id || '' },
          { display_name: 'Booking ID', variable_name: 'booking_id', value: String(bookingId ?? '') },
          {
            display_name: 'Original Booking ID',
            variable_name: 'original_booking_id',
            value: String(paystackOriginalBookingId ?? ''),
          },
        ],
      },
    };
  }, [
    paystackReference,
    paystackEmail,
    paystackAmount,
    amountToPayNow,
    user?.id,
    draftBookingId,
    paystackOriginalBookingId,
  ]);

  const initializePayment = usePaystackPayment(paystackConfig);

  const getPaystackReferenceFromSuccess = (payload) => {
    return (
      payload?.reference ||
      payload?.trxref ||
      payload?.trxRef ||
      paystackReferenceRef.current ||
      paystackReference ||
      paystackConfig.reference ||
      null
    );
  };

  const verifyPaystackPayment = async (reference) => {
    const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
    if (sessionErr) throw sessionErr;

    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) throw new Error('Not authenticated. Please log in again and retry payment.');

    const { data, error } = await supabase.functions.invoke('verify-paystack-booking', {
      body: { reference },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (error) throw error;
    if (!data || !data.ok) throw new Error(data?.message || 'Payment verification failed at the server.');

    return data;
  };

  const preparePaystackBookingPayment = async ({
    bookingId,
    reference,
    walletAmount,
    originalBookingId = null,
  }) => {
    const { data, error } = await supabase.rpc('prepare_my_booking_card_payment_v1', {
      p_booking_id: bookingId,
      p_reference: reference,
      p_wallet_amount: moneyNumber(walletAmount),
      p_original_booking_id: originalBookingId,
    });

    if (error) throw error;
    if (!data?.ok) {
      throw new Error(data?.message || 'Could not prepare secure card payment.');
    }

    return data;
  };

  /* -------------------------------------------------------
     Draft Booking + Promo Helpers
  -------------------------------------------------------- */
  const clearDraftState = () => {
    setDraftBookingId(null);
    setDraftBooking(null);
    setPromoCode('');
    setPromoMsg(null);
    setAppliedPromo(null);
    setPaystackAmount(null);
    setPaystackReference(null);
    setPendingPaystackLaunch(null);
    paystackBookingIdRef.current = null;
    walletAppliedRef.current = 0;
    paystackReferenceRef.current = null;
    paystackOpeningRef.current = false;
  };

  const fetchBookingRow = async (bookingId) => {
    const { data, error } = await supabase.from('bookings').select('*').eq('booking_id', bookingId).single();
    if (error) throw error;
    return data;
  };

  const getExpectedFareForSelectedOption = () => getOptionFare(selectedOption, basePrice);

  const enforceExpectedBookingFare = async (bookingId, row = null) => {
    if (!bookingId || !selectedOption) return row || null;

    const current = row || (await fetchBookingRow(bookingId));
    const finalTripFare = getExpectedFareForSelectedOption();

    // Do not override an already-discounted booking downward/upward after a promo was redeemed.
    const hasDiscount = moneyNumber(current?.discount_amount) > 0 || !!current?.discount_code;
    if (hasDiscount) return current;

    const currentFare = moneyNumber(current?.final_price ?? current?.price ?? 0);
    const currentOriginal = moneyNumber(current?.original_price ?? 0);

    const fareNeedsFix = Math.abs(currentFare - finalTripFare) >= 0.01;
    const originalNeedsFix = Math.abs(currentOriginal - finalTripFare) >= 0.01;

    if (!fareNeedsFix && !originalNeedsFix) return current;

    const arrears = moneyNumber(current?.arrears_due ?? debtAmount ?? 0);
    const credit = Math.max(moneyNumber(current?.credit_applied), moneyNumber(current?.wallet_paid));
    const paid = moneyNumber(current?.paid_amount);
    const paymentStatus = String(current?.payment_status || '').toLowerCase();
    const isPaid = paymentStatus === 'paid' || paid > 0;

    const updatePayload = {
      price: finalTripFare,
      final_price: finalTripFare,
      original_price: finalTripFare,
      amount_due: isPaid ? 0 : Math.max(0, finalTripFare + arrears - credit),
    };

    const { error } = await supabase.from('bookings').update(updatePayload).eq('booking_id', bookingId);
    if (error) throw error;

    return await fetchBookingRow(bookingId);
  };

  const getLatestBookingAmounts = async (bookingId) => {
    const latestRaw = await fetchBookingRow(bookingId);
    const latest = await enforceExpectedBookingFare(bookingId, latestRaw);

    const finalTripFare = getExpectedFareForSelectedOption();
    const latestFare = moneyNumber(latest?.final_price ?? latest?.price ?? 0);
    const hasDiscount = moneyNumber(latest?.discount_amount) > 0 || !!latest?.discount_code;

    const fareAmount = hasDiscount ? latestFare : Math.max(latestFare, finalTripFare);
    const arrearsAmount = moneyNumber(latest?.arrears_due ?? debtAmount ?? 0);
    const totalOwedBeforeWallet = Math.max(0, fareAmount + arrearsAmount);

    return { latest, fareAmount, arrearsAmount, totalOwedBeforeWallet };
  };

  const applyWalletIfNeeded = async (bookingId, amount) => {
    const walletAmount = moneyNumber(amount);
    if (walletAmount <= 0) return;

    const { error } = await supabase.rpc('apply_wallet_credit_to_booking_v1', {
      p_booking_id: bookingId,
      p_amount: walletAmount,
    });

    if (error) throw error;
  };


  const persistStructuredBookingLocations = async (bookingId) => {
    if (!bookingId) return;

    const selectedLocationPayload = buildStructuredLocationPayload(preciseLocation);
    if (!selectedLocationPayload) return;

    try {
      const { error } = await supabase.rpc('save_my_booking_structured_locations_v1', {
        p_booking_id: String(bookingId),
        p_pickup: tripType === 'dropoff' ? selectedLocationPayload : null,
        p_dropoff: tripType === 'pickup' ? selectedLocationPayload : null,
      });

      // This is intentionally non-blocking. If the optional migration is not
      // deployed yet, the established booking, payment and matching flow still
      // completes using its existing text and navigation-coordinate fields.
      if (error) {
        console.warn('Structured booking location could not be saved yet:', error.message || error);
      }
    } catch (locationError) {
      console.warn('Structured booking location persistence failed:', locationError);
    }
  };

  const buildConfirmedPaymentPayload = ({ finalMethod, fareAmount, arrearsAmount, walletCreditApplied, cardPaid = 0 }) => {
    const totalOwedBeforeWallet = Math.max(0, moneyNumber(fareAmount) + moneyNumber(arrearsAmount));
    const walletPaid = Math.min(moneyNumber(walletCreditApplied), totalOwedBeforeWallet);
    const dueAfterWallet = Math.max(0, totalOwedBeforeWallet - walletPaid);

    // A booking that is being restored/confirmed is operationally active again.
    // Never carry cancellation/expiry metadata forward from an earlier checkout
    // expiry or recovered payment attempt. The database trigger enforces the
    // same invariant for webhook/server-side recovery paths.
    const activeLifecycleReset = {
      cancelled_at: null,
      cancel_reason: null,
      cancelled_reason: null,
      expired_at: null,
      cancellation_category: null,
    };

    if (finalMethod === 'wallet') {
      return {
        ...activeLifecycleReset,
        status: 'confirmed',
        payment_method: 'wallet',
        payment_status: 'paid',
        paid_at: new Date().toISOString(),
        paid_amount: totalOwedBeforeWallet,
        wallet_paid: walletPaid,
        card_paid: 0,
        amount_due: 0,
        credit_applied: walletPaid,
        price: fareAmount,
        final_price: fareAmount,
        arrears_due: arrearsAmount,
      };
    }

    if (finalMethod === 'card') {
      return {
        ...activeLifecycleReset,
        status: 'confirmed',
        payment_method: 'card',
        payment_status: 'paid',
        paid_at: new Date().toISOString(),
        paid_amount: totalOwedBeforeWallet,
        wallet_paid: walletPaid,
        card_paid: moneyNumber(cardPaid || dueAfterWallet),
        amount_due: 0,
        credit_applied: walletPaid,
        price: fareAmount,
        final_price: fareAmount,
        arrears_due: arrearsAmount,
      };
    }

    return {
      ...activeLifecycleReset,
      status: 'confirmed',
      payment_method: 'cash',
      payment_status: 'unpaid',
      paid_at: null,
      paid_amount: 0,
      wallet_paid: walletPaid,
      card_paid: 0,
      amount_due: dueAfterWallet,
      credit_applied: walletPaid,
      price: fareAmount,
      final_price: fareAmount,
      arrears_due: arrearsAmount,
    };
  };

  const ensureDraftBooking = async (method) => {
    if (!canBookTrips) {
      void trackAirportBookingEvent('booking_validation_failed', 'VERIFICATION_REQUIRED');
      showNotification('error', 'Annual student verification required before booking.');
      navigate('/profile');
      return null;
    }

    if (!marketCanBook) {
      void trackAirportBookingEvent('booking_validation_failed', 'MARKET_CLOSED');
      showNotification('info', marketBookingMessage);
      return null;
    }

    if (draftBookingId) return draftBookingId;
    if (!selectedOption) return null;

    if (!hasPreciseLocation) {
      void trackAirportBookingEvent('booking_validation_failed', 'INVALID_LOCATION');
      showNotification(
        'error',
        tripType === 'dropoff'
          ? 'Please choose a specific pickup address, residence, building, or pin location.'
          : 'Please choose a specific destination address, residence, building, or pin location.'
      );
      return null;
    }

    if (!user?.market_id) {
      void trackAirportBookingEvent('booking_validation_failed', 'ACCOUNT_MARKET_MISSING');
      showNotification('error', 'Your account market is missing. Please contact support.');
      return null;
    }

    if (!activeRouteProduct?.route_product_id) {
      void trackAirportBookingEvent('booking_validation_failed', 'PRODUCT_UNAVAILABLE');
      showNotification('error', 'Trip product is unavailable for this route.');
      return null;
    }

    const normalizedFlightNumber = normalizeFlightNumber(flightNumber);

    if (requiresFlightNumber && !normalizedFlightNumber) {
      void trackAirportBookingEvent('booking_validation_failed', 'MISSING_FLIGHT_NUMBER');
      showNotification('error', 'Please enter your flight number.');
      return null;
    }

    const finalTripFare = getExpectedFareForSelectedOption();
    const finalDate = date || new Date().toISOString().split('T')[0];
    const actualPickup = tripType === 'pickup' ? calculatedTime : time;

    const airportBufferMinutes =
      tripType === 'pickup'
        ? getAirportPickupBufferMinutes({
            tripMode: tripType,
            flightType,
            hasCheckInLuggage: hasLuggage,
          })
        : 0;

    const payload = {
      p_client_id: user.id,
      p_driver_id: selectedOption.driver_id,
      p_availability_id: selectedOption.availability_id,
      p_market_id: user.market_id,
      p_zone_id: tripType === 'dropoff' ? bookingZoneId || null : null,
      p_route_product_id: activeRouteProduct.route_product_id,
      p_trip_type: tripType,
      p_booking_date: finalDate,
      p_requested_time: time,
      p_actual_pickup_time: actualPickup,
      p_pickup_location: tripType === 'dropoff' ? getBookingLocationText(preciseLocation, location) : 'Cape Town International Airport',
      p_dropoff_location: tripType === 'dropoff' ? 'Cape Town International Airport' : getBookingLocationText(preciseLocation, location),
      p_zone: tripType === 'dropoff' ? bookingZoneName : null,
      p_has_luggage: hasLuggage,
      p_flight_type: tripType === 'pickup' ? normalizeFlightType(flightType) : null,
      p_airport_buffer_minutes: tripType === 'pickup' ? airportBufferMinutes : null,
      p_flight_number: normalizedFlightNumber || null,
      p_toc_version: TOC_VERSION,
      p_ride_mode: selectedOption.type,
      p_payment_method: method,
      p_price: finalTripFare,
      p_original_price: finalTripFare,
    };

    try {
      const navigationPayload = {
        p_pickup_label: tripType === 'dropoff' ? normaliseBookingLocation(preciseLocation)?.label || null : null,
        p_pickup_latitude: tripType === 'dropoff' ? Number(normaliseBookingLocation(preciseLocation)?.latitude) : null,
        p_pickup_longitude: tripType === 'dropoff' ? Number(normaliseBookingLocation(preciseLocation)?.longitude) : null,
        p_pickup_landmark_code: tripType === 'pickup' ? 'airport_pickup' : null,

        p_dropoff_label: tripType === 'pickup' ? normaliseBookingLocation(preciseLocation)?.label || null : null,
        p_dropoff_latitude: tripType === 'pickup' ? Number(normaliseBookingLocation(preciseLocation)?.latitude) : null,
        p_dropoff_longitude: tripType === 'pickup' ? Number(normaliseBookingLocation(preciseLocation)?.longitude) : null,
        p_dropoff_landmark_code: tripType === 'dropoff' ? 'airport_departures' : null,
      };

      const { data: newId, error: rpcErr } = await supabase.rpc(
        'ensure_booking_draft_with_network_dispatch_v2',
        {
          ...payload,
          ...navigationPayload,
        },
      );
      if (rpcErr) throw rpcErr;

      await persistStructuredBookingLocations(newId);
      setDraftBookingId(newId);

      const row = await enforceExpectedBookingFare(newId);
      setDraftBooking(row);

      return newId;
    } catch (err) {
      console.error('Draft Booking Error:', err);

      showNotification(
        'error',
        err.message === 'Annual student verification required before booking'
          ? 'Annual student verification required before booking. Please re-verify your student email.'
          : err.message || 'Could not create booking draft.'
      );

      return null;
    }
  };

  const applyPromoToBooking = async (bookingId, code) => {
    if (!canBookTrips) throw new Error('Annual student verification required before booking');

    const cleaned = (code || '').trim().toUpperCase();
    if (!cleaned) throw new Error('Enter a promo code');

    const existing = await fetchBookingRow(bookingId);
    const existingCode = String(existing?.discount_code || '').trim().toUpperCase();
    const existingDiscount = moneyNumber(existing?.discount_amount);

    // If the same draft already has this promo, do not apply it again.
    // This prevents BOOKING_ALREADY_HAS_PROMO after a Paystack refresh/retry.
    if (existingCode && existingCode === cleaned && existingDiscount > 0) {
      setDraftBooking(existing);
      return { rpc: { ok: true, already_applied: true }, booking: existing };
    }

    if (existingCode && existingCode !== cleaned) {
      throw new Error(`This booking already has promo code ${existingCode} applied.`);
    }

    const { data, error } = await supabase.rpc('apply_booking_promo', {
      p_booking_id: bookingId,
      p_code: cleaned,
    });

    if (error) throw error;

    const fresh = await fetchBookingRow(bookingId);

    if (String(fresh.payment_method || '').toLowerCase() === 'cash') {
      const fare = moneyNumber(fresh.final_price ?? fresh.price);
      const arrears = moneyNumber(fresh.arrears_due);
      const credit = Math.max(moneyNumber(fresh.credit_applied), moneyNumber(fresh.wallet_paid));

      await supabase
        .from('bookings')
        .update({ amount_due: Math.max(0, fare + arrears - credit) })
        .eq('booking_id', bookingId);
    }

    const latest = await fetchBookingRow(bookingId);
    setDraftBooking(latest);

    return { rpc: data, booking: latest };
  };

  const getPromoCodeToRedeem = () =>
    String(appliedPromo?.promo_code || appliedPromo?.code || promoCode || '')
      .trim()
      .toUpperCase();

  const redeemPreviewedPromoOrThrow = async (bookingId) => {
    // No promo preview means there is nothing to redeem.
    if (!appliedPromo) return null;

    const code = getPromoCodeToRedeem();

    if (!code) {
      throw new Error('PROMO_CODE_MISSING_FROM_PREVIEW');
    }

    const result = await applyPromoToBooking(bookingId, code);
    const booking = result?.booking || (await fetchBookingRow(bookingId));

    const storedCode = String(booking?.discount_code || '')
      .trim()
      .toUpperCase();
    const storedDiscount = moneyNumber(booking?.discount_amount);
    const storedRedemptionId = booking?.promo_redemption_id;

    // Never allow a preview-only discount to reach cash confirmation or Paystack.
    // A valid booking promo must leave the booking with a complete audit trail.
    if (
      booking?.discount_applied !== true ||
      storedCode !== code ||
      storedDiscount <= 0 ||
      !storedRedemptionId
    ) {
      throw new Error('PROMO_NOT_SECURELY_SAVED');
    }

    setDraftBooking(booking);
    return booking;
  };

  const getFriendlyPromoError = (e) => {
    const rawMessage = String(e?.message || '');

    return rawMessage.includes('PROMO_NOT_SECURELY_SAVED')
      ? 'Your promo could not be securely saved to this booking. Payment was stopped. Please apply the code again.'
      : rawMessage.includes('PROMO_CODE_MISSING_FROM_PREVIEW')
      ? 'Your promo preview is incomplete. Please apply the code again.'
      : rawMessage.includes('CAMPAIGN_PROMO_LIMIT_REACHED')
      ? 'You’ve already used a discount code for this campaign. Only one campaign discount is allowed per student.'
      : rawMessage.includes('STUDENT_REVERIFICATION_REQUIRED') ||
        rawMessage.includes('Annual student verification required before booking')
      ? 'Annual student verification required before booking. Please re-verify your student email.'
      : rawMessage.includes('PROMO_CODE_REQUIRED')
      ? 'Enter a promo code first.'
      : rawMessage.includes('INVALID_OR_INACTIVE_PROMO_CODE')
      ? 'This promo code is invalid or inactive.'
      : rawMessage.includes('PROMO_NOT_VALID_FOR_BOOKINGS')
      ? 'This promo code cannot be used for bookings.'
      : rawMessage.includes('PROMO_NOT_STARTED_YET')
      ? 'This promo code is not active yet.'
      : rawMessage.includes('PROMO_EXPIRED')
      ? 'This promo code has expired.'
      : rawMessage.includes('PROMO_MAX_TOTAL_USES_REACHED')
      ? 'This promo code has reached its usage limit.'
      : rawMessage.includes('PROMO_MAX_USES_PER_USER_REACHED')
      ? 'You’ve already used this promo code.'
      : rawMessage.includes('BOOKING_ALREADY_HAS_PROMO')
      ? 'A promo code has already been applied to this booking.'
      : rawMessage.includes('BOOKING_PRICE_BELOW_PROMO_MINIMUM')
      ? 'This booking does not meet the minimum amount required for this promo.'
      : rawMessage.includes('PROMO_NOT_ALLOWED_FOR_BOOKING_STATUS')
      ? 'A promo cannot be applied to this booking anymore.'
      : rawMessage.includes('BOOKING_NOT_FOUND_OR_NOT_YOURS')
      ? 'Could not find this booking. Please refresh and try again.'
      : rawMessage || 'Could not apply promo code. Please try again.';
  };

  const handleApplyPromo = async () => {
    if (!canBookTrips) {
      showNotification('error', 'Annual student verification required before booking.');
      navigate('/profile');
      return;
    }

    const code = (promoCode || '').trim();

    if (!code) return showNotification('error', 'Enter a promo code first.');
    if (!selectedOption) return showNotification('error', 'Please select a ride option first.');

    const originalAmount = moneyNumber(selectedOption.price || basePrice || 0);

    if (originalAmount <= 0) {
      return showNotification('error', 'Could not calculate the trip price. Please go back and select the ride again.');
    }

    setPromoMsg(null);
    setPromoLoading(true);

    try {
      const { data, error } = await supabase.rpc('preview_booking_promo', {
        p_code: code,
        p_original_amount: originalAmount,
      });

      if (error) throw error;

      // Normalise the preview response. Some older preview responses return
      // `code` rather than `promo_code`; the real redemption step must always
      // receive a canonical promo_code before payment can continue.
      const normalizedPromoCode = String(data?.promo_code || data?.code || code)
        .trim()
        .toUpperCase();

      if (!normalizedPromoCode) {
        throw new Error('PROMO_CODE_MISSING_FROM_PREVIEW');
      }

      const normalizedPromo = {
        ...(data || {}),
        promo_code: normalizedPromoCode,
      };

      setAppliedPromo(normalizedPromo);
      setPromoCode(normalizedPromoCode);
      setPromoMsg({ type: 'success', text: 'Promo applied ✅' });
    } catch (e) {
      setAppliedPromo(null);
      setPromoMsg({
        type: 'error',
        text: getFriendlyPromoError(e),
      });
    } finally {
      setPromoLoading(false);
    }
  };

  /* -------------------------------------------------------
     Step 1: Search Options
  -------------------------------------------------------- */
  const handleStep1Submit = (e) => {
    e.preventDefault();

    if (!user) return showNotification('error', 'You must be logged in to book.');

    if (!canBookTrips) {
      void trackAirportBookingEvent('booking_validation_failed', 'VERIFICATION_REQUIRED');
      showNotification('error', 'Annual student verification required before booking.');
      navigate('/profile');
      return;
    }

    if (!user?.market_id) {
      void trackAirportBookingEvent('booking_validation_failed', 'ACCOUNT_MARKET_MISSING');
      return showNotification('error', 'Your account market is missing. Please contact support.');
    }
    if (tripType === 'dropoff' && departureContextLoading) {
      return showNotification('info', 'Checking this pickup area...');
    }
    if (tripType === 'dropoff' && isExternalDeparture && !isExternalDepartureReady) {
      void trackAirportBookingEvent('booking_validation_failed', 'EXTERNAL_AREA_UNAVAILABLE');
      return showNotification(
        'info',
        departureContext?.message || 'This Airport Transfer pickup area is not available yet.',
      );
    }
    if (tripType === 'dropoff' && !isExternalDeparture && !zoneId) {
      void trackAirportBookingEvent('booking_validation_failed', 'MISSING_ZONE');
      return showNotification('error', 'Please select your suburb/zone.');
    }

    if (!hasPreciseLocation) {
      void trackAirportBookingEvent('booking_validation_failed', 'INVALID_LOCATION');
      return showNotification(
        'error',
        tripType === 'dropoff'
          ? 'Please choose a specific pickup address, residence, building, or pin location.'
          : 'Please choose a specific destination address, residence, building, or pin location.'
      );
    }

    const normalizedFlightNumber = normalizeFlightNumber(flightNumber);

    if (requiresFlightNumber && !normalizedFlightNumber) {
      void trackAirportBookingEvent('booking_validation_failed', 'MISSING_FLIGHT_NUMBER');
      return showNotification('error', 'Please enter your flight number.');
    }

    if (requiresFlightNumber && normalizedFlightNumber !== flightNumber) {
      setFlightNumber(normalizedFlightNumber);
    }

    const normalizedTime =
      tripType === 'dropoff'
        ? roundTimeToNearestSlot(time, DROPOFF_SLOT_INTERVAL_MINUTES)
        : time;

    if (tripType === 'dropoff' && normalizedTime !== time) {
      setTime(normalizedTime);
    }

    const finalTime = calculateActualPickupTime({
      tripMode: tripType,
      requestedTime: normalizedTime,
      hasCheckInLuggage: hasLuggage,
      flightType,
    });

    setCalculatedTime(finalTime);

    fetchOptions({
      reqDate: date,
      reqTime: normalizedTime,
      computedPickupTime: finalTime,
      tripMode: tripType,
      reqZoneId: bookingZoneId,
      reqZoneName: bookingZoneName,
      pickupLocation: preciseLocation,
    });
  };

  const fetchOptions = async ({
    reqDate,
    reqTime,
    computedPickupTime,
    tripMode,
    reqZoneId,
    reqZoneName,
    pickupLocation,
  }) => {
    if (!canBookTrips) {
      showNotification('error', 'Annual student verification required before booking.');
      navigate('/profile');
      return;
    }

    void trackAirportBookingEvent('availability_search_started');

    setLoading(true);
    setAvailableOptions([]);
    setAlternativeTimes([]);
    setSearchedTimeSummary(null);

    try {
      if (!user?.market_id) throw new Error('User market is missing.');

      const safeDate = reqDate;
      const safeTime = (tripMode === 'pickup' ? computedPickupTime : reqTime)?.slice(0, 5) || '12:00';
      let sharedBasePrice = 0;
      let privateBasePrice = 0;

      try {
        const sharedQuote = await getLiveBasePrice({ tripMode, rideMode: 'shared', reqZoneId, driverId: null });
        sharedBasePrice = sharedQuote.basePrice;
      } catch (e) {
        console.warn('Shared quote unavailable:', e.message);
      }

      try {
        const privateQuote = await getLiveBasePrice({ tripMode, rideMode: 'private', reqZoneId, driverId: null });
        privateBasePrice = privateQuote.basePrice;
        setBasePrice(privateBasePrice);
      } catch (e) {
        console.warn('Private quote unavailable:', e.message);
      }

      setSearchedTimeSummary({
        date: safeDate,
        requestedTime: reqTime,
        actualSearchTime: safeTime,
        tripMode,
        zone: reqZoneName,
      });

      let shareableRides = [];

      if (tripMode === 'dropoff' && isExternalDeparture) {
        if (departureContext?.allow_shared_rides === true) {
          const point = normaliseBookingLocation(pickupLocation);
          const { data, error: shareError } = await supabase.rpc(
            'find_shareable_airport_departure_rides_v1',
            {
              p_search_date: safeDate,
              p_search_time: safeTime,
              p_booking_market_id: user.market_id,
              p_pickup_latitude: point?.latitude ?? null,
              p_pickup_longitude: point?.longitude ?? null,
            },
          );
          if (shareError) throw shareError;
          shareableRides = data || [];
        }
      } else {
        const { data, error: shareError } = await supabase.rpc('find_shareable_rides_v4', {
          _date: safeDate,
          _time: safeTime,
          _trip_type: tripMode,
          _market_id: user.market_id,
          _zone_id: tripMode === 'dropoff' ? reqZoneId || null : null,
        });
        if (shareError) throw shareError;
        shareableRides = data || [];
      }

      let formattedShared = (shareableRides || []).map((r) => ({
        type: 'shared',
        id: `share_${r.booking_id}`,
        booking_id: r.booking_id,
        driver_id: r.driver_id,
        availability_id: r.availability_id,
        car_make: r.car_make,
        car_model: r.car_model,
        car_color: r.car_color,
        license_plate: r.license_plate,
        name: r.first_name,
        last_name: r.last_name,
        phone_number: r.phone_number,
        photo: r.driver_profile_photo_url,
        degree_program: r.degree_program || r.driver_degree_program || r.study_program || r.programme || null,
        university_name: r.university_name || r.driver_university_name || null,
        university_short_code: r.university_short_code || null,
        home_market_id: r.home_market_id || null,
        home_market_name: r.home_market_name || null,
        operating_market_id: r.operating_market_id || null,
        operating_market_name: r.operating_market_name || null,
        availability_market_id: r.availability_market_id || user.market_id || null,
        dispatch_corridor_id: r.dispatch_corridor_id || null,
        is_cross_market_candidate: r.is_cross_market_candidate === true,
        airport_departure_area_id: r.airport_departure_area_id || null,
        rating: r.average_rating ? Number(r.average_rating) : null,
        car: buildVehicleLabel(r),
        vehicle_photo_url: r.vehicle_photo_url,
        seats_available: r.seats_available,
        time: r.current_pickup_time,
        price: sharedBasePrice || privateBasePrice || 0,
        delay: Math.abs(r.delay_minutes || 0),
      }));

      formattedShared = await attachDriverDisplayIdentities(formattedShared);

      const p1 = formattedShared.filter((s) => s.delay <= 5);

      if (p1.length > 0) {
        trackAirportAvailabilityShown();
        setAvailableOptions(p1);
        setStep(2);
        return;
      }

      if (formattedShared.length > 0) {
        trackAirportAvailabilityShown();
        setAvailableOptions(formattedShared);
        setStep(2);
        return;
      }

      const pickupPoint = normaliseBookingLocation(pickupLocation);
      const { data: emptyDrivers, error: emptyError } = await supabase.rpc('get_available_airport_drivers_v6', {
        p_search_date: safeDate,
        p_search_time: safeTime,
        p_trip_mode: tripMode || 'pickup',
        p_service_market_id: user.market_id,
        p_zone_id: tripMode === 'dropoff' ? reqZoneId || null : null,
        p_pickup_latitude: tripMode === 'dropoff' ? pickupPoint?.latitude ?? null : null,
        p_pickup_longitude: tripMode === 'dropoff' ? pickupPoint?.longitude ?? null : null,
      });

      if (emptyError) throw emptyError;

      if (process.env.NODE_ENV !== 'production') {
        console.table(
          (emptyDrivers || []).map((d) => ({
            driver_id: d.driver_id,
            first_name: d.first_name,
            last_name: d.last_name,
            vehicle_id: d.vehicle_id,
            vehicle_assignment_id:
              d.vehicle_assignment_id || d.assignment_id || d.active_assignment_id || d.current_assignment_id || null,
            ownership_type: d.ownership_type,
            operating_model: d.operating_model || d.vehicle_operating_model,
            assignment_type: d.assignment_type,
            assignment_state: d.assignment_state,
            vehicle_source: d.vehicle_source || d.vehicle_context || d.source,
            car_make: d.car_make,
            car_model: d.car_model,
            license_plate: d.license_plate,
            availability_market_id: d.availability_market_id || null,
            is_cross_market_candidate: d.is_cross_market_candidate === true,
          }))
        );
      }

      const uniqueEmptyDrivers = dedupeAvailableDrivers(emptyDrivers || []);

      let formattedEmpty = uniqueEmptyDrivers.map((d) => ({
        type: 'private',
        id: `${d.driver_id}_${d.availability_id || 'availability'}_${d.vehicle_id || 'vehicle'}`,
        driver_id: d.driver_id,
        name: d.first_name,
        last_name: d.last_name,
        phone_number: d.phone_number,
        photo: d.profile_photo_url,
        degree_program: d.degree_program || d.driver_degree_program || d.study_program || d.programme || null,
        university_name: d.university_name || d.driver_university_name || null,
        university_short_code: d.university_short_code || null,
        home_market_id: d.home_market_id || null,
        home_market_name: d.home_market_name || null,
        operating_market_id: d.operating_market_id || null,
        operating_market_name: d.operating_market_name || null,
        availability_market_id: d.availability_market_id || user.market_id || null,
        dispatch_corridor_id: d.dispatch_corridor_id || null,
        is_cross_market_candidate: d.is_cross_market_candidate === true,
        airport_departure_area_id: d.airport_departure_area_id || null,

        car_make: d.car_make,
        car_model: d.car_model,
        car_color: d.car_color,
        license_plate: d.license_plate,
        vehicle_photo_url: d.vehicle_photo_url,
        seats_available: d.seats_available,
        vehicle_id: d.vehicle_id,
        vehicle_assignment_id:
          d.vehicle_assignment_id || d.assignment_id || d.active_assignment_id || d.current_assignment_id || null,
        ownership_type: d.ownership_type,
        operating_model: d.operating_model || d.vehicle_operating_model,
        assignment_type: d.assignment_type,
        assignment_state: d.assignment_state,
        vehicle_source: d.vehicle_source || d.vehicle_context || d.source,

        car: buildVehicleLabel(d),
        rating: d.rating,
        time: d.start_time,
        price: privateBasePrice || 0,
        availability_id: d.availability_id,
      }));

      formattedEmpty = await attachDriverDisplayIdentities(formattedEmpty);

      if (formattedEmpty.length > 0) {
        trackAirportAvailabilityShown();
        setAvailableOptions(formattedEmpty);
        setStep(2);
        return;
      }

      const nearbyTimes = await fetchAlternativeTimes({
        reqDate: safeDate,
        reqTime,
        computedPickupTime,
        tripMode,
        reqZone: reqZoneId,
        pickupLocation,
      });

      const noAvailabilityReasonCode =
        nearbyTimes.length > 0
          ? 'NO_DRIVER_AT_SELECTED_TIME'
          : 'NO_DRIVER_AVAILABLE';

      const selectedPassengerLocation = recoveryLocationPayload(
        pickupLocation,
        location,
      );
      const airportEndpoint = {
        label: 'Cape Town International Airport',
        formatted_address: 'Cape Town International Airport',
        airport_code: 'CPT',
      };
      const origin = tripMode === 'dropoff'
        ? selectedPassengerLocation
        : airportEndpoint;
      const destination = tripMode === 'dropoff'
        ? airportEndpoint
        : selectedPassengerLocation;
      const quotedFare = Math.max(
        moneyNumber(sharedBasePrice),
        moneyNumber(privateBasePrice),
      );

      // Record the normal funnel event first, then save the richer recovery
      // snapshot last. This prevents the synchroniser from replacing the
      // detailed date, time and route with the generic funnel event.
      try {
        await trackAirportBookingEvent(
          'no_availability_found',
          noAvailabilityReasonCode,
        );
      } catch (trackingError) {
        console.warn(
          '[Journey Recovery] Standard no-availability tracking failed:',
          trackingError?.message || trackingError,
        );
      }

      await recordImmediateNoAvailabilityRecovery({
        marketId: user?.market_id || null,
        productCode: 'airport_transfers',
        userId: user?.id,
        reasonCode: noAvailabilityReasonCode,
        pageSource: 'client_booking',
        journeyContext: {
          booking_date: safeDate,
          requested_date: safeDate,
          requested_time: safeTime,
          passenger_selected_time: reqTime,
          actual_search_time: safeTime,
          flight_landing_time: tripMode === 'pickup' ? reqTime : null,
          trip_direction: tripMode === 'dropoff' ? 'to_airport' : 'from_airport',
          route_label: `${origin?.label || 'Pickup'} → ${destination?.label || 'Destination'}`,
          origin,
          destination,
          zone_id: reqZoneId || null,
          zone_name: reqZoneName || null,
          passenger_count: 1,
          flight_number: normalizeFlightNumber(flightNumber) || null,
          flight_type: tripMode === 'pickup' ? normalizeFlightType(flightType) : null,
          has_check_in_luggage: Boolean(hasLuggage),
          airport_buffer_minutes:
            tripMode === 'pickup'
              ? getAirportPickupBufferMinutes({
                  tripMode,
                  flightType,
                  hasCheckInLuggage: hasLuggage,
                })
              : 0,
          fare_shown: quotedFare > 0 ? quotedFare : null,
          pricing_options: {
            shared_base_price: moneyNumber(sharedBasePrice) || null,
            private_base_price: moneyNumber(privateBasePrice) || null,
          },
          alternative_times_offered: (nearbyTimes || []).slice(0, 8).map((slot) => ({
            slot_time: slot?.slot_time || null,
            driver_count: Number(slot?.driver_count || 0),
            direction: slot?.direction || null,
            minutes_diff: Number(slot?.minutes_diff || 0),
          })),
          reason_code: noAvailabilityReasonCode,
        },
      });

      setAlternativeTimes(nearbyTimes);
      setAvailableOptions([]);
      setStep(2);
    } catch (err) {
      console.error(err);
      void trackAirportBookingEvent(
        'booking_validation_failed',
        inferBookingFunnelReasonCode(err, 'NETWORK_ERROR'),
      );
      showNotification('error', err.message || 'Could not fetch options');
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------
     Booking Save Logic
  -------------------------------------------------------- */
  const handleConfirmClick = async () => {
    if (!canBookTrips) {
      void trackAirportBookingEvent('booking_validation_failed', 'VERIFICATION_REQUIRED');
      showNotification('error', 'Annual student verification required before booking.');
      navigate('/profile');
      return;
    }

    void trackAirportBookingEvent('booking_submit_clicked');

    if (!agreedToTerms) {
      void trackAirportBookingEvent('booking_validation_failed', 'TERMS_NOT_ACCEPTED');
      return showNotification('error', 'Accept Terms of Carriage first.');
    }

    if (!selectedOption) {
      void trackAirportBookingEvent('booking_validation_failed', 'NO_OPTION_SELECTED');
      return;
    }

    const normalizedFlightNumber = normalizeFlightNumber(flightNumber);

    if (requiresFlightNumber && !normalizedFlightNumber) {
      void trackAirportBookingEvent('booking_validation_failed', 'MISSING_FLIGHT_NUMBER');
      return showNotification('error', 'Please enter your flight number.');
    }

    if (requiresFlightNumber && normalizedFlightNumber !== flightNumber) {
      setFlightNumber(normalizedFlightNumber);
    }

    const isPriority1 = selectedOption.type === 'shared' && selectedOption.delay <= 5;

    if (selectedOption.type === 'shared' && !isPriority1) {
      await saveBookingToSupabase({ method: paymentMethod, walletApplied: creditToApply });
      return;
    }

    if (paymentMethod === 'card') {
      await handleCardPaymentFlow();
    } else {
      const isFullWallet = creditToApply >= totalBeforeWallet && totalBeforeWallet > 0;
      await saveBookingToSupabase({ method: isFullWallet ? 'wallet' : 'cash', walletApplied: creditToApply });
    }
  };

  const handleCardPaymentFlow = async () => {
    if (!canBookTrips) {
      showNotification('error', 'Annual student verification required before booking.');
      navigate('/profile');
      return;
    }

    if (paystackOpeningRef.current) return;

    // A saved-card authorisation may have been submitted before the app
    // refreshed. Do not prepare a different Paystack reference for the same
    // fare while the previous attempt could still settle.
    try {
      const previous=JSON.parse(sessionStorage.getItem(PENDING_CARD_PAYMENT_KEY)||'null');
      if(previous?.savedCardAttempt && previous.reference){
        const {data,error}=await supabase.functions.invoke('uniair-saved-card-checkout',{
          body:{action:'verify',product:'airport',payment_reference:previous.reference},
        });
        if(data?.status==='not_started'){
          sessionStorage.removeItem(PENDING_CARD_PAYMENT_KEY);
        }else if(data?.status==='paid'){
          sessionStorage.removeItem(PENDING_CARD_PAYMENT_KEY);
          showNotification('success','Your earlier saved-card booking payment is confirmed.');
          navigate('/my-bookings?tab=upcoming');
          return;
        }else{
          showNotification('info',data?.message||error?.message||
            'Your earlier saved-card payment is still being checked. Do not pay again.');
          return;
        }
      }
    }catch {
      showNotification('info',
        'Your previous saved-card payment could not be checked. Please do not pay again yet.');
      return;
    }

    setPaymentCloseNotice(false);
    setPaymentUiStage('preparing');
    setLoading(true);

    try {
      const finalDate = date || new Date().toISOString().split('T')[0];
      const isPriority1Shared = selectedOption.type === 'shared' && selectedOption.delay <= 5;

      let activeBookingId = draftBookingId;

      if (!activeBookingId) activeBookingId = await ensureDraftBooking('card');

      if (!activeBookingId) {
        setPaymentUiStage('idle');
        setLoading(false);
        return;
      }

      // Redeem and verify the promo before calculating the Paystack amount.
      // This blocks a preview-only discount from ever reaching payment.
      await redeemPreviewedPromoOrThrow(activeBookingId);

      const { fareAmount, arrearsAmount, totalOwedBeforeWallet } = await getLatestBookingAmounts(activeBookingId);
      const walletApplied = Math.min(creditToApply, totalOwedBeforeWallet);
      const finalPayNow = Math.max(0, totalOwedBeforeWallet - walletApplied);

      paystackBookingIdRef.current = activeBookingId;
      walletAppliedRef.current = walletApplied;

      if (finalPayNow <= 0) {
        setPaymentUiStage('idle');

        if (selectedOption.type === 'private') {
          await applyWalletIfNeeded(activeBookingId, walletApplied);

          const { error } = await supabase
            .from('bookings')
            .update(
              buildConfirmedPaymentPayload({
                finalMethod: 'wallet',
                fareAmount,
                arrearsAmount,
                walletCreditApplied: walletApplied,
              })
            )
            .eq('booking_id', activeBookingId);

          if (error) throw error;

          clearDraftState();
          goToBookingSuccess({
            booking_id: activeBookingId,
            price: totalOwedBeforeWallet,
            method: 'wallet',
            dateStr: finalDate,
          });

          return;
        }

        await saveBookingToSupabase({ method: 'wallet', walletApplied });
        return;
      }

      const generatedReference = `MS_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;

      const preparedPayment = await preparePaystackBookingPayment({
        bookingId: activeBookingId,
        reference: generatedReference,
        walletAmount: walletApplied,
        originalBookingId: isPriority1Shared ? selectedOption.booking_id : null,
      });

      const preparedTotal = moneyNumber(preparedPayment?.total_amount);
      const preparedWallet = moneyNumber(preparedPayment?.wallet_amount);
      const preparedCard = moneyNumber(preparedPayment?.card_amount);

      // Do not silently charge an amount different from the checkout the passenger saw.
      if (
        Math.abs(preparedTotal - totalOwedBeforeWallet) >= 0.01 ||
        Math.abs(preparedWallet - walletApplied) >= 0.01 ||
        Math.abs(preparedCard - finalPayNow) >= 0.01
      ) {
        throw new Error(
          'Your booking amount or wallet balance changed. Please review the total and try again.'
        );
      }

      // Card saving is opt-in and tied to this exact prepared Airport payment.
      // Record the choice server-side before the Paystack popup opens.
      const {data: cardConsent, error: cardConsentError} = await supabase.rpc(
        'uniair_set_airport_card_save_consent_v1',{
          p_reference: generatedReference, p_save: Boolean(saveCardForFuture && airportCardSource === 'new'),
        });
      if (cardConsentError || !cardConsent?.ok) {
        throw new Error('Could not confirm your saved-card preference. Please try again.');
      }

      sessionStorage.setItem(
        PENDING_CARD_PAYMENT_KEY,
        JSON.stringify({
          bookingId: activeBookingId,
          reference: generatedReference,
          amount: preparedCard,
          createdAt: new Date().toISOString(),
          returnPath: "/book",
          savedCardAttempt: airportCardSource==='saved' && Boolean(airportSelectedCardId),
        })
      );

      paystackReferenceRef.current = generatedReference;
      setPaystackReference(generatedReference);
      setPaystackAmount(preparedCard);

      if (airportCardSource === 'saved' && airportSelectedCardId) {
        // This one-time Paystack authorisation uses the prepared fare/credit
        // amount and the existing server-side booking finaliser. A network
        // error must NOT trigger a second card charge or open the card popup.
        const {data:result,error:chargeError} = await supabase.functions.invoke(
          'uniair-saved-card-checkout',{
            body:{action:'charge',product:'airport',
              payment_reference:generatedReference,
              saved_card_id:airportSelectedCardId},
          });
        if (result?.status === 'paid') {
          sessionStorage.removeItem(PENDING_CARD_PAYMENT_KEY);
          clearDraftState();
          goToBookingSuccess({
            booking_id:activeBookingId,price:preparedTotal,
            method:'card',dateStr:finalDate,
          });
          return;
        }
        if (result?.status === 'requires_action' && result.challenge_url) {
          const challenge=new URL(result.challenge_url);
          if (challenge.protocol==='https:' && challenge.hostname==='checkout.paystack.com') {
            window.location.assign(challenge.toString());
            return;
          }
        }
        // Do not open a second checkout when a charge is uncertain.
        setPaymentUiStage('confirming');
        showNotification('info', result?.message ||
          (chargeError ? 'Your saved-card payment is being checked. Please do not pay again.' :
            'Checking your saved-card payment. Please do not pay again.'));
        setLoading(false);
        return;
      }

      setPendingPaystackLaunch({
        activeBookingId,
        walletApplied: preparedWallet,
        totalBefore: preparedTotal,
        fareAmount,
        arrearsAmount,
        finalDate,
        isPriority1Shared,
        finalPayNow: preparedCard,
      });
    } catch (e) {
      console.error('[Card Flow] error:', e);

      void trackAirportBookingEvent(
        'payment_failed',
        inferBookingFunnelReasonCode(e, 'PAYMENT_FAILED'),
        paystackBookingIdRef.current || draftBookingId,
      );

      showNotification('error', getFriendlyPromoError(e) || e.message || 'Card payment failed.');

      paystackOpeningRef.current = false;
      paystackReferenceRef.current = null;
      setPaystackReference(null);
      setPendingPaystackLaunch(null);
      setPaymentUiStage('idle');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!pendingPaystackLaunch) return;
    if (paystackOpeningRef.current) return;

    const {
      activeBookingId,
      totalBefore,
      finalDate,
      finalPayNow,
    } = pendingPaystackLaunch;

    if (!paystackReference) return;
    if (moneyNumber(paystackAmount) !== moneyNumber(finalPayNow)) return;

    const cents = Math.round(moneyNumber(finalPayNow) * 100);
    const pk = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_live_dd59be131e1a5b5c9ae0ee8a40bf628292fcc757';

    if (!pk || !String(pk).startsWith('pk_')) {
      void trackAirportBookingEvent(
        'payment_failed',
        'PAYSTACK_CONFIGURATION_ERROR',
        activeBookingId,
      );
      showNotification('error', 'Paystack public key is missing/invalid.');
      setPendingPaystackLaunch(null);
      setPaymentUiStage('idle');
      setLoading(false);
      return;
    }

    if (!Number.isFinite(cents) || cents <= 0) {
      void trackAirportBookingEvent(
        'payment_failed',
        'INVALID_PAYMENT_AMOUNT',
        activeBookingId,
      );
      showNotification('error', `Invalid Paystack amount: ${finalPayNow}`);
      setPendingPaystackLaunch(null);
      setPaymentUiStage('idle');
      setLoading(false);
      return;
    }

    paystackOpeningRef.current = true;
    setPaymentUiStage('opening');

    void trackAirportBookingEvent('payment_started', null, activeBookingId);

    initializePayment({
      onSuccess: async (refObj) => {
        setPaymentCloseNotice(false);
        setPaymentUiStage('confirming');

        try {
          const ref = getPaystackReferenceFromSuccess(refObj);

          if (!ref) {
            throw new Error('Payment completed, but no payment reference was returned.');
          }

          const verifiedPayment = await verifyPaystackPayment(ref);
          sessionStorage.removeItem(PENDING_CARD_PAYMENT_KEY);

          if (verifiedPayment?.server_finalized === true) {
            const finalizedBookingId =
              Number(verifiedPayment?.booking_id) || activeBookingId;

            clearDraftState();

            goToBookingSuccess({
              booking_id: finalizedBookingId,
              price: totalBefore,
              method: 'card',
              dateStr: finalDate,
            });

            return;
          }

          // Paystack has been verified and Finance has recorded the charge.
          // The browser must never try to "finish" the money state itself.
          if (verifiedPayment?.verified === true && verifiedPayment?.recorded === true) {
            clearDraftState();
            sessionStorage.removeItem('uniair_booking_state');

            navigate('/dashboard', {
              state: {
                message:
                  'Payment received. UniAir is finalising your booking. Do not pay again. Your booking will update automatically.',
              },
            });
            return;
          }

          throw new Error('Payment could not be verified by UniAir.');
        } catch (e) {
          console.error('[Paystack Success] handling failed:', e);

          void trackAirportBookingEvent(
            'payment_failed',
            inferBookingFunnelReasonCode(e, 'PAYMENT_FAILED'),
            activeBookingId,
          );

          showNotification(
            'error',
            e.message === 'Annual student verification required before booking'
              ? 'Annual student verification required before booking. Please re-verify your student email.'
              : e.message || 'Payment success handling failed.'
          );
        } finally {
          paystackOpeningRef.current = false;
          paystackReferenceRef.current = null;
          setPaystackReference(null);
          setPendingPaystackLaunch(null);
          setPaystackAmount(null);
          setPaymentUiStage('idle');
          setLoading(false);
        }
      },
      onClose: () => {
        // Closing the Paystack UI is not authoritative evidence that payment
        // failed. A 3DS/card payment may already have succeeded while the
        // browser success callback is still settling.
        //
        // Record the close for funnel analysis only. Do NOT mutate/cancel the
        // booking here. The backend 15-minute expiry owns true abandonment.
        void trackAirportBookingEvent(
          'payment_cancelled',
          'PAYSTACK_WINDOW_CLOSED',
          activeBookingId,
        );

        sessionStorage.removeItem(PENDING_CARD_PAYMENT_KEY);

        // Keep the same pending booking draft in React state. If the passenger
        // retries, we reopen payment against the SAME booking rather than
        // creating another abandoned draft.
        paystackOpeningRef.current = false;
        paystackReferenceRef.current = null;
        paystackBookingIdRef.current = activeBookingId;
        walletAppliedRef.current = 0;

        setPaystackReference(null);
        setPendingPaystackLaunch(null);
        setPaystackAmount(null);
        setPaymentUiStage('idle');
        setPaymentCloseNotice(true);
        setLoading(false);

        showNotification(
          'info',
          'Payment window closed. If you approved the payment in your banking app, give UniAir a moment to confirm it. Otherwise you can retry securely without starting your booking again.'
        );
      },
    });
  }, [
    pendingPaystackLaunch,
    paystackReference,
    paystackAmount,
    paystackConfig,
    initializePayment,
    selectedOption,
    user?.id,
    tripType,
    location,
  ]);

  const saveBookingToSupabase = async ({ method, walletApplied = 0 }) => {
    if (!canBookTrips) {
      showNotification('error', 'Annual student verification required before booking.');
      navigate('/profile');
      return;
    }

    setLoading(true);

    try {
      let activeBookingId = draftBookingId;

      if (!activeBookingId) activeBookingId = await ensureDraftBooking(method);

      if (!activeBookingId) {
        setLoading(false);
        return;
      }

      // Redeem and verify the promo before cash/wallet confirmation or a shared request.
      // This blocks a preview-only discount from being saved without its code.
      await redeemPreviewedPromoOrThrow(activeBookingId);

      const finalDate = date || new Date().toISOString().split('T')[0];
      const { fareAmount, arrearsAmount, totalOwedBeforeWallet } = await getLatestBookingAmounts(activeBookingId);

      const walletCreditApplied = Math.min(Math.max(0, moneyNumber(walletApplied)), totalOwedBeforeWallet);
      const finalMethod =
        walletCreditApplied >= totalOwedBeforeWallet && totalOwedBeforeWallet > 0 ? 'wallet' : method;

      const isPriority1 = selectedOption.type === 'shared' && selectedOption.delay <= 5;

      if (selectedOption.type === 'shared') {
        if (isPriority1) {
          const { data: newBookingId, error } = await joinSharedRide({
            _original_booking_id: selectedOption.booking_id,
            _requesting_client_id: user.id,
            _payment_method: finalMethod,
            _amount: totalOwedBeforeWallet,
            _pickup_loc: tripType === 'dropoff' ? getBookingLocationText(preciseLocation, location) : 'Cape Town International Airport',
            _dropoff_loc: tripType === 'dropoff' ? 'Cape Town International Airport' : getBookingLocationText(preciseLocation, location),
            _draft_booking_id: activeBookingId,
          });

          if (error) throw error;

          await persistStructuredBookingLocations(newBookingId);
          await applyWalletIfNeeded(newBookingId, walletCreditApplied);

          const { error: updateErr } = await supabase
            .from('bookings')
            .update(
              buildConfirmedPaymentPayload({
                finalMethod,
                fareAmount,
                arrearsAmount,
                walletCreditApplied,
              })
            )
            .eq('booking_id', newBookingId);

          if (updateErr) throw updateErr;

          clearDraftState();

          goToBookingSuccess({
            booking_id: newBookingId,
            price: totalOwedBeforeWallet,
            method: finalMethod,
            dateStr: finalDate,
          });

          return;
        }

        const { error } = await supabase.rpc('request_shared_ride_v4', {
          _original_booking_id: selectedOption.booking_id,
          _requesting_client_id: user.id,
          _flight_arrival_time: time,
          _actual_pickup_time: tripType === 'pickup' ? calculatedTime : time,
          _has_luggage: hasLuggage,
          _flight_type: tripType === 'pickup' ? normalizeFlightType(flightType) : null,
          _airport_buffer_minutes:
            tripType === 'pickup'
              ? getAirportPickupBufferMinutes({
                  tripMode: tripType,
                  flightType,
                  hasCheckInLuggage: hasLuggage,
                })
              : 0,
          _payment_method: finalMethod,
          _amount: totalOwedBeforeWallet,
          _pickup_loc: tripType === 'dropoff' ? getBookingLocationText(preciseLocation, location) : 'Cape Town International Airport',
          _dropoff_loc: tripType === 'dropoff' ? 'Cape Town International Airport' : getBookingLocationText(preciseLocation, location),
          _draft_booking_id: activeBookingId,
        });

        if (error) throw error;

        sessionStorage.removeItem('uniair_booking_state');
        void trackAirportBookingEvent('booking_created', null, activeBookingId);
        void resolveMyJourneyRecoveryAfterBooking({
          productCode: 'airport_transfers',
          entityType: 'booking_request',
          entityId: activeBookingId,
        });

        navigate('/dashboard', {
          state: {
            message: finalMethod === 'card' ? 'Request sent! Pay to confirm once accepted.' : 'Request sent! Waiting for approval.',
          },
        });

        return;
      }

      await applyWalletIfNeeded(activeBookingId, walletCreditApplied);

      const { error } = await supabase
        .from('bookings')
        .update(
          buildConfirmedPaymentPayload({
            finalMethod,
            fareAmount,
            arrearsAmount,
            walletCreditApplied,
          })
        )
        .eq('booking_id', activeBookingId);

      if (error) throw error;

      const after = await fetchBookingRow(activeBookingId);
      const afterFare = moneyNumber(after.final_price ?? after.price);
      const afterCredit = Math.max(moneyNumber(after.credit_applied), moneyNumber(after.wallet_paid));

      const expectedDue =
        String(finalMethod) === 'cash' ? Math.max(0, afterFare + moneyNumber(after.arrears_due) - afterCredit) : 0;

      if (moneyNumber(after.amount_due) !== expectedDue) {
        await supabase.from('bookings').update({ amount_due: expectedDue }).eq('booking_id', activeBookingId);
      }

      clearDraftState();

      goToBookingSuccess({
        booking_id: activeBookingId,
        price: totalOwedBeforeWallet,
        method: finalMethod,
        dateStr: finalDate,
      });
    } catch (err) {
      console.error(err);

      void trackAirportBookingEvent(
        method === 'card' ? 'payment_failed' : 'booking_validation_failed',
        inferBookingFunnelReasonCode(
          err,
          method === 'card' ? 'PAYMENT_FAILED' : 'REQUEST_FAILED',
        ),
        draftBookingId,
      );

      showNotification(
        'error',
        err.message === 'Annual student verification required before booking'
          ? 'Annual student verification required before booking. Please re-verify your student email.'
          : getFriendlyPromoError(err) || err.message || 'Booking failed'
      );
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------
     Student re-verification lock
  -------------------------------------------------------- */
  if (marketLaunchLoading && canBookTrips) {
    return (
      <div className="relative min-h-screen bg-gray-50 px-4 py-6 sm:py-8" style={UNIAIR_BOOKING_TYPOGRAPHY}>
        {notification && <NotificationBanner notification={notification} />}

        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-maties-burgundy">
              ← Dashboard
            </button>
            <h1 className="text-2xl font-bold text-maties-burgundy">Book a Shuttle</h1>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-200 text-center">
            <div className="text-4xl mb-4">⏳</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Checking availability</h2>
            <p className="text-sm text-gray-600 max-w-lg mx-auto">
              We are checking whether bookings are open for your university.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (canBookTrips && user?.market_id && marketLaunchContext?.ok && !marketCanBook) {
    return (
      <div className="relative min-h-screen bg-gray-50 px-4 py-6 sm:py-8" style={UNIAIR_BOOKING_TYPOGRAPHY}>
        {notification && <NotificationBanner notification={notification} />}

        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-maties-burgundy">
              ← Dashboard
            </button>
            <h1 className="text-2xl font-bold text-maties-burgundy">Book a Shuttle</h1>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-md border border-amber-200">
            <div className="text-center">
              <div className="text-5xl mb-4">🚐</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">Bookings are not open yet</h2>
              <p className="text-sm text-gray-600 max-w-lg mx-auto mb-4">
                {marketBookingMessage}
              </p>

              {marketLaunchContext?.university_name && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left mb-6">
                  <p className="font-bold text-amber-800 mb-1">{marketLaunchContext.university_name}</p>
                  <p className="text-sm text-amber-700">
                    Current launch stage: {String(marketLaunchContext.launch_status || '').replace(/_/g, ' ')}.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-maties-burgundy text-white py-4 rounded-xl font-bold hover:bg-red-900 transition"
                >
                  Back to Dashboard
                </button>
                <button
                  onClick={() => navigate('/driver-register')}
                  className="w-full bg-white border border-gray-200 text-gray-700 py-4 rounded-xl font-bold hover:bg-gray-50 transition"
                >
                  Apply as a Driver
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (requiresStudentReverification || !canBookTrips) {
    return (
      <div className="relative min-h-screen bg-gray-50 px-4 py-6 sm:py-8" style={UNIAIR_BOOKING_TYPOGRAPHY}>
        {notification && <NotificationBanner notification={notification} />}

        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-maties-burgundy">
              ← Dashboard
            </button>
            <h1 className="text-2xl font-bold text-maties-burgundy">Book a Shuttle</h1>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-md border border-red-200">
            <div className="text-center">
              <div className="text-5xl mb-4">🎓</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">Annual student verification required</h2>
              <p className="text-sm text-gray-600 max-w-lg mx-auto mb-4">
                Your yearly student verification has expired, so booking is temporarily unavailable until you verify your
                student email again.
              </p>

              {verificationExpiryLabel && (
                <p className="text-sm text-red-700 font-bold mb-2">Previous verification expired on {verificationExpiryLabel}.</p>
              )}

              {typeof studentVerificationDaysRemaining === 'number' && studentVerificationDaysRemaining <= 0 && (
                <p className="text-xs text-gray-500 mb-6">
                  Re-verification keeps UniAir student-only and helps us maintain trust and safety on the platform.
                </p>
              )}

              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-left mb-6">
                <p className="font-bold text-red-800 mb-1">Booking temporarily locked</p>
                <p className="text-sm text-red-700">
                  Please re-verify your student email from your profile before creating or confirming any booking.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => navigate('/profile')}
                  className="w-full bg-maties-burgundy text-white py-4 rounded-xl font-bold hover:bg-red-900 transition"
                >
                  Verify again
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-white border border-gray-200 text-gray-700 py-4 rounded-xl font-bold hover:bg-gray-50 transition"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gray-50 px-4 py-6 sm:py-8" style={UNIAIR_BOOKING_TYPOGRAPHY}>
      {notification && <NotificationBanner notification={notification} />}

      <div className="max-w-2xl mx-auto">
        {step !== 3 && (
          <div className="mb-6 flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="text-sm font-medium text-gray-600 transition hover:text-maties-burgundy"
            >
              Back to dashboard
            </button>
            <h1 className="text-xl font-bold tracking-tight text-maties-burgundy">Airport ride</h1>
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleStep1Submit} className="space-y-4">
            <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.10em] text-gray-400">Airport ride</p>
              <h2 className="mt-1 text-xl font-bold tracking-tight text-gray-900">Plan your journey</h2>
              <p className="mt-1 text-sm leading-5 text-gray-500">
                Choose your direction, set the exact location and we will show the available rides.
              </p>

              <div className="mt-4 grid grid-cols-2 gap-2 rounded-2xl bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => handleTripTypeChange('dropoff')}
                  className={`rounded-xl px-3 py-3 text-sm font-semibold transition ${
                    tripType === 'dropoff'
                      ? 'bg-[#720e1e] text-white shadow-sm'
                      : 'bg-transparent text-gray-500 hover:text-gray-800'
                  }`}
                >
                  To Airport
                </button>
                <button
                  type="button"
                  onClick={() => handleTripTypeChange('pickup')}
                  className={`rounded-xl px-3 py-3 text-sm font-semibold transition ${
                    tripType === 'pickup'
                      ? 'bg-[#720e1e] text-white shadow-sm'
                      : 'bg-transparent text-gray-500 hover:text-gray-800'
                  }`}
                >
                  From Airport
                </button>
              </div>
            </section>

            <AirportJourneyCard
              tripType={tripType}
              selectedLocation={preciseLocation}
              locationText={location}
              disabled={loading}
              onEditLocation={handleOpenLocationPicker}
              onSelectLocation={validateInlineAirportLocation}
              onLocationTextChange={handleLocationTextChange}
            />

            {tripType === 'dropoff' && !isExternalDeparture && (
              <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <label htmlFor="booking-zone" className="block text-xs font-semibold uppercase tracking-[0.10em] text-gray-400">
                  Suburb / zone
                </label>
                <select
                  id="booking-zone"
                  value={zoneId}
                  onChange={(e) => setZoneId(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-medium text-gray-900 outline-none transition focus:border-[#720e1e] focus:ring-2 focus:ring-[#720e1e]/15 disabled:cursor-not-allowed disabled:bg-gray-100"
                  disabled={zonesLoading}
                  required
                >
                  <option value="">Select your suburb</option>
                  {zones.map((z) => (
                    <option key={z.zone_id} value={z.zone_id}>
                      {z.name}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs leading-5 text-gray-500">
                  Your suburb helps us match you with the right shared rides.
                </p>
              </section>
            )}

            {tripType === 'dropoff' && isExternalDeparture && (
              <section
                className={`rounded-2xl border p-4 shadow-sm ${
                  isExternalDepartureReady
                    ? 'border-[#ead7c1] bg-[#fffaf3]'
                    : 'border-amber-200 bg-amber-50'
                }`}
              >
                <p className="text-xs font-semibold uppercase tracking-[0.10em] text-gray-400">
                  Pickup area
                </p>
                <p className="mt-1 text-base font-bold text-gray-900">
                  {departureContext?.label || 'Cape Town Airport Transfer area'}
                </p>
                <p className="mt-2 text-xs leading-5 text-gray-600">
                  {isExternalDepartureReady
                    ? 'Your exact pickup pin is inside an approved Airport Transfer area. We will only show drivers currently verified in this area.'
                    : departureContext?.message || 'This Airport Transfer pickup area is not available yet.'}
                </p>
              </section>
            )}

            <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                <div className="pr-3">
                  <label htmlFor="booking-date" className="block text-xs font-semibold uppercase tracking-[0.10em] text-gray-400">
                    Date
                  </label>
                  <input
                    id="booking-date"
                    type="date"
                    value={date}
                    onChange={(e) => handleTravelDateChange(e.target.value)}
                    className="mt-2 w-full bg-transparent text-sm font-medium text-gray-900 outline-none"
                    required
                  />
                </div>

                <div className="pl-3">
                  <label className="block text-xs font-semibold uppercase tracking-[0.10em] text-gray-400">
                    {tripType === 'pickup' ? 'Flight lands at' : 'Pickup time'}
                  </label>

                  {tripType === 'dropoff' ? (
                    <div className="mt-2 grid grid-cols-[1fr_auto_1fr] items-center gap-1">
                      <select
                        value={dropoffSelectedHour}
                        onChange={(e) => setTime(`${e.target.value}:${dropoffSelectedMinute}`)}
                        className="min-w-0 bg-transparent text-center text-sm font-medium text-gray-900 outline-none"
                        aria-label="Pickup hour"
                        required
                      >
                        {DROPOFF_SLOT_HOUR_OPTIONS.map((hour) => (
                          <option key={hour} value={hour}>
                            {hour}
                          </option>
                        ))}
                      </select>
                      <span className="text-sm font-semibold text-gray-400">:</span>
                      <select
                        value={dropoffSelectedMinute}
                        onChange={(e) => setTime(`${dropoffSelectedHour}:${e.target.value}`)}
                        className="min-w-0 bg-transparent text-center text-sm font-medium text-gray-900 outline-none"
                        aria-label="Pickup minutes"
                        required
                      >
                        {DROPOFF_SLOT_MINUTE_OPTIONS.map((minute) => (
                          <option key={minute} value={minute}>
                            {minute}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      className="mt-2 w-full bg-transparent text-sm font-medium text-gray-900 outline-none"
                      required
                    />
                  )}
                </div>
              </div>

              {tripType === 'dropoff' && (
                <p className="mt-3 border-t border-gray-100 pt-3 text-xs leading-5 text-gray-500">
                  Select your preferred pickup time in 15-minute intervals. We will show the closest available rides.
                </p>
              )}
            </section>

            {tripType === 'pickup' && (
              <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.10em] text-gray-400">Flight details</p>
                  <p className="mt-1 text-sm leading-5 text-gray-500">
                    We use these details to calculate a realistic airport collection time.
                  </p>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  {FLIGHT_TYPE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFlightType(option.value)}
                      className={`rounded-xl border-2 px-3 py-3 text-sm font-semibold transition ${
                        normalizeFlightType(flightType) === option.value
                          ? 'border-[#720e1e] bg-[#720e1e]/5 text-[#720e1e]'
                          : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className="mt-4 flex items-center justify-between gap-4 border-y border-gray-100 py-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Check-in luggage</p>
                    <p className="mt-1 text-xs leading-5 text-gray-500">Adds time for baggage collection before your pickup.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setHasLuggage((current) => !current)}
                    className={`relative h-7 w-12 shrink-0 rounded-full transition ${
                      hasLuggage ? 'bg-[#720e1e]' : 'bg-gray-200'
                    }`}
                    role="switch"
                    aria-checked={hasLuggage}
                    aria-label="Check-in luggage"
                  >
                    <span
                      className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
                        hasLuggage ? 'left-6' : 'left-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="mt-4">
                  <label htmlFor="flight-number" className="block text-xs font-semibold uppercase tracking-[0.10em] text-gray-400">
                    Flight number {requiresFlightNumber ? '*' : ''}
                  </label>
                  <input
                    id="flight-number"
                    type="text"
                    value={flightNumber}
                    onChange={(e) => setFlightNumber(normalizeFlightNumber(e.target.value))}
                    placeholder="For example, SA317"
                    className="mt-2 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-medium uppercase text-gray-900 outline-none transition focus:border-[#720e1e] focus:ring-2 focus:ring-[#720e1e]/15"
                    required={requiresFlightNumber}
                  />
                  <p className="mt-2 text-xs leading-5 text-gray-500">This helps your driver follow your arrival before collection.</p>
                </div>

                {actualPickupTimeForSelectedInputs && (
                  <div className="mt-4 rounded-xl border border-[#720e1e]/15 bg-[#720e1e]/5 px-3 py-3 text-sm text-[#5a0b18]">
                    <p className="font-semibold">Estimated driver pickup: {actualPickupTimeForSelectedInputs}</p>
                    <p className="mt-1 text-xs leading-5">
                      Based on a {normalizeFlightType(flightType)} flight with{' '}
                      {hasLuggage ? 'checked luggage' : 'no checked luggage'}.
                      {airportBufferMinutesForSelectedInputs > 0 && (
                        <> We added {airportBufferMinutesForSelectedInputs} minutes for airport procedures.</>
                      )}
                    </p>
                  </div>
                )}
              </section>
            )}

            <div className="border-t border-gray-200 bg-white px-1 pt-4">
              <button
                type="submit"
                disabled={loading || departureContextLoading}
                className="w-full rounded-2xl bg-[#720e1e] px-4 py-4 text-base font-semibold tracking-[-0.01em] text-white shadow-sm transition hover:bg-red-950 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading
                  ? 'Searching for rides...'
                  : departureContextLoading
                    ? 'Checking pickup area...'
                    : 'Find rides'}
              </button>
              <p className="mt-3 text-center text-xs leading-5 text-gray-400">
                Availability is checked using a realistic operating window of about {OP_BLOCK_MINUTES} minutes.
              </p>
            </div>
          </form>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-800">Select an Option</h2>

            {availableOptions.length === 0 ? (
              <NoOptionsCard
                searchedTimeSummary={searchedTimeSummary}
                alternativeTimes={alternativeTimes}
                tripType={tripType}
                buildWhatsAppLink={buildWhatsAppLink}
                retryWithSuggestedTime={retryWithSuggestedTime}
                onBack={() => setStep(1)}
                onCheckAgain={() =>
                  fetchOptions({
                    reqDate: date,
                    reqTime: time,
                    computedPickupTime: calculatedTime || time,
                    tripMode: tripType,
                    reqZoneId: bookingZoneId,
                    reqZoneName: bookingZoneName,
                    pickupLocation: preciseLocation,
                  })
                }
              />
            ) : (
              availableOptions.map((opt) => {
                const driverName = `${opt.name || ''} ${opt.last_name || ''}`.trim() || 'UniAir Driver';
                const studyLabel = getDriverStudyLabel(opt);
                const universityLabel = getDriverUniversityLabel(opt);
                const operatingMarketLabel = getDriverOperatingMarketLabel(opt);
                const isOperatingOutsideHome =
                  operatingMarketLabel &&
                  operatingMarketLabel !== String(opt.home_market_name || '').trim();
                const vehicleLabel = buildVehicleLabel(opt);
                const optionPrice = moneyNumber(opt.price);

                return (
                  <button
                    type="button"
                    key={opt.id}
                    onClick={() => {
                      clearDraftState();
                      setSelectedOption(opt);
                      setPaymentMethod('cash');
                      setUseWallet(true);
                      setStep(3);
                    }}
                    className={`w-full rounded-2xl shadow-sm border cursor-pointer transition text-left overflow-hidden active:scale-[0.99] ${
                      opt.type === 'shared'
                        ? 'bg-[#fffaf3] border-[#ead7c1] hover:border-[#720e1e]'
                        : 'bg-white border-gray-100 hover:border-[#720e1e]'
                    }`}
                  >
                    <div className="p-3 sm:p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 sm:w-18 sm:h-18 bg-gray-200 rounded-2xl overflow-hidden flex items-center justify-center shrink-0 shadow-sm border border-gray-100">
                          {opt.photo ? (
                            <img src={opt.photo} className="w-full h-full object-cover" alt={driverName} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                              </svg>
                            </div>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5 mb-1">
                            <span className="text-[9px] uppercase tracking-[0.10em] text-gray-400 font-bold">
                              {opt.type === 'shared' ? 'Join Ride' : 'Driver'}
                            </span>

                            {opt.type === 'shared' && (
                              <span className="text-[9px] bg-[#720e1e] text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                                Shared
                              </span>
                            )}

                            {opt.type === 'shared' && opt.delay <= 5 && (
                              <span className="text-[9px] bg-[#f7e8cf] text-[#720e1e] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border border-[#d6b37a]">
                                Instant
                              </span>
                            )}
                          </div>

                          <h3 className="font-bold text-gray-900 text-base sm:text-lg leading-tight break-words">
                            {driverName}
                          </h3>

                          {universityLabel ? (
                            <p className="text-[11px] sm:text-xs text-[#720e1e] font-semibold mt-0.5 leading-snug break-words">
                              {universityLabel}
                            </p>
                          ) : null}

                          {studyLabel ? (
                            <p className="text-[11px] sm:text-xs text-gray-600 font-medium mt-0.5 leading-snug break-words">
                              {studyLabel}
                            </p>
                          ) : !universityLabel ? (
                            <p className="text-[11px] sm:text-xs text-gray-400 font-bold mt-0.5 leading-snug">
                              Verified student driver
                            </p>
                          ) : null}

                          {isOperatingOutsideHome ? (
                            <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#f7e8cf] px-2 py-0.5 text-[10px] font-bold text-[#7b5512]">
                              <span className="h-1.5 w-1.5 rounded-full bg-[#d6ad60]" aria-hidden="true" />
                              Operating in {operatingMarketLabel}
                            </p>
                          ) : null}

                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <p className="text-xs sm:text-sm text-gray-600 font-semibold leading-snug break-words">
                              {vehicleLabel}
                            </p>

                            {typeof opt.rating === 'number' && !Number.isNaN(opt.rating) && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-[#720e1e]/10 text-[#720e1e] px-2 py-0.5 text-[11px] font-bold">
                                <span>★</span>
                                <span>{opt.rating.toFixed(1)}</span>
                              </span>
                            )}
                          </div>

                          {opt.type === 'shared' && (
                            <p className="text-[11px] text-gray-500 mt-1 leading-5">
                              Your requested pickup stays{' '}
                              <b>{String(tripType === 'pickup' ? calculatedTime || time : time).slice(0, 5)}</b>.
                              {Number(opt.delay || 0) > 5 ? (
                                <>
                                  {' '}We&apos;ll ask the driver and current passenger to approve a{' '}
                                  <b>{Math.abs(Number(opt.delay || 0))} min</b> timing adjustment.
                                </>
                              ) : (
                                <> {' '}This ride can be shared around your selected time.</>
                              )}
                            </p>
                          )}
                        </div>

                        <div className="shrink-0 text-right self-stretch flex flex-col items-end justify-between gap-2">
                          <div>
                            <p className="text-[9px] uppercase tracking-[0.10em] text-gray-400 font-bold">
                              Fare
                            </p>
                            <p className="text-lg sm:text-xl font-bold text-gray-900 whitespace-nowrap">
                              R {optionPrice.toFixed(2)}
                            </p>
                          </div>

                          <span className="rounded-full bg-[#720e1e] text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide">
                            Select
                          </span>
                        </div>
                      </div>

                    </div>
                  </button>
                );
              })
            )}

            <button onClick={() => setStep(1)} className="w-full mt-4 text-gray-500 font-bold">
              ← Back
            </button>
          </div>
        )}

        {step === 3 && selectedOption && (
          <section className="pb-4 sm:pb-8">
            <div className="mb-5 flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setStep(2);
                  clearDraftState();
                }}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-100 bg-white text-gray-600 shadow-sm transition hover:bg-gray-50 hover:text-[#720e1e]"
                aria-label="Back to ride options"
              >
                <ChevronLeft className="h-5 w-5" strokeWidth={2.25} />
              </button>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">Airport ride</p>
                <h2 className="text-xl font-bold tracking-tight text-gray-900">
                  {selectedOption.type === 'shared' && selectedOption.delay > 5 ? 'Request to join ride' : 'Confirm booking'}
                </h2>
              </div>
            </div>

            <div className="space-y-4">
              <section className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
                <dl className="space-y-4">
                  <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-3">
                    <dt className="pt-0.5 text-sm font-medium text-gray-500">Trip</dt>
                    <dd className="min-w-0 text-right text-sm font-semibold leading-5 text-gray-900">
                      <span className="block break-words">{confirmationRouteLabel}</span>
                      <span className="mt-1 block text-xs font-medium text-gray-500">
                        {confirmationDateLabel} · {confirmationMainTime || 'Time pending'}
                      </span>
                    </dd>
                  </div>

                  <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-3">
                    <dt className="text-sm font-medium text-gray-500">Driver</dt>
                    <dd className="min-w-0 text-right text-sm font-semibold text-gray-900">{confirmationDriverName}</dd>
                  </div>

                  <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-3">
                    <dt className="text-sm font-medium text-gray-500">Type</dt>
                    <dd className="min-w-0 text-right text-sm font-semibold capitalize text-gray-900">{confirmationRideType}</dd>
                  </div>
                </dl>
              </section>

              {tripType === 'pickup' && (
                <section className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-400">Flight details</p>
                  <dl className="mt-4 space-y-3">
                    <div className="flex items-center justify-between gap-4 text-sm">
                      <dt className="text-gray-500">Flight lands at</dt>
                      <dd className="text-right font-semibold text-gray-900">{time || 'Not set'}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-4 text-sm">
                      <dt className="text-gray-500">Flight type</dt>
                      <dd className="text-right font-semibold capitalize text-gray-900">{normalizeFlightType(flightType)}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-4 text-sm">
                      <dt className="text-gray-500">Driver pickup time</dt>
                      <dd className="text-right font-semibold text-gray-900">{calculatedTime || actualPickupTimeForSelectedInputs || 'Pending'}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-4 text-sm">
                      <dt className="text-gray-500">Airport buffer</dt>
                      <dd className="text-right font-semibold text-gray-900">{airportBufferMinutesForSelectedInputs} min</dd>
                    </div>
                    {!!normalizeFlightNumber(flightNumber) && (
                      <div className="flex items-center justify-between gap-4 text-sm">
                        <dt className="text-gray-500">Flight number</dt>
                        <dd className="text-right font-semibold text-gray-900">{normalizeFlightNumber(flightNumber)}</dd>
                      </div>
                    )}
                  </dl>
                </section>
              )}

              <PromoPanel
                draftBooking={draftBooking}
                appliedPromo={appliedPromo}
                promoCode={promoCode}
                setPromoCode={setPromoCode}
                promoLoading={promoLoading}
                promoMsg={promoMsg}
                handleApplyPromo={handleApplyPromo}
                clearDraftState={clearDraftState}
              />

              <WalletPanel
                walletBalance={walletBalance}
                debtAmount={debtAmount}
                useWallet={useWallet}
                setUseWallet={setUseWallet}
                creditToApply={creditToApply}
              />

              <section className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-gray-500">Base fare</span>
                    <span className="font-semibold text-gray-900">{moneyText(selectedBaseFare)}</span>
                  </div>

                  {displayedDiscountAmount > 0 && (
                    <div className="flex items-center justify-between gap-4 text-green-700">
                      <span>Promo discount</span>
                      <span className="font-semibold">- {moneyText(displayedDiscountAmount)}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-4 border-t border-gray-100 pt-3">
                    <span className="font-semibold text-gray-900">Fare</span>
                    <span className="font-semibold text-gray-900">{moneyText(effectiveFare)}</span>
                  </div>

                  {debtAmount > 0 && (
                    <div className="flex items-center justify-between gap-4 border-t border-red-100 pt-3 text-red-700">
                      <span>Outstanding balance</span>
                      <span className="font-semibold">+ {moneyText(debtAmount)}</span>
                    </div>
                  )}
                </div>

                <div className="mt-5 flex items-center justify-between gap-4 border-t border-gray-100 pt-4">
                  <span className="text-lg font-semibold text-gray-900">
                    {paymentMethod === 'card' && (selectedOption.type === 'private' || selectedOption.delay <= 5)
                      ? 'Pay now'
                      : 'Total'}
                  </span>
                  <span className="text-2xl font-bold text-[#720e1e]">{moneyText(amountToPayNow)}</span>
                </div>
              </section>

              <section>
                <p className="mb-3 text-sm font-semibold text-gray-700">Payment method</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setPaymentCloseNotice(false);
                      setPaymentMethod('cash');
                    }}
                    className={`flex min-h-[58px] items-center justify-center gap-2 rounded-2xl border-2 px-3 py-3 text-sm font-semibold transition ${
                      paymentMethod === 'cash'
                        ? 'border-green-600 bg-green-50 text-green-800'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Banknote className="h-5 w-5" strokeWidth={1.9} />
                    Cash
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setPaymentCloseNotice(false);
                      setPaymentMethod('card');
                    }}
                    className={`flex min-h-[58px] items-center justify-center gap-2 rounded-2xl border-2 px-3 py-3 text-sm font-semibold transition ${
                      paymentMethod === 'card'
                        ? 'border-[#720e1e] bg-[#720e1e]/5 text-[#720e1e]'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <CreditCard className="h-5 w-5" strokeWidth={1.9} />
                    Card
                  </button>
                </div>

                {paymentMethod === 'card' && amountToPayNow > 0 &&
                  (selectedOption.type === 'private' || selectedOption.delay <= 5) &&
                  airportSavedCards.length > 0 && (
                    <div className="mt-3 space-y-2 rounded-2xl border border-gray-200 p-3">
                      <p className="text-sm font-semibold text-gray-900">Choose a card</p>
                      <label className="flex items-center gap-2 text-xs font-medium text-gray-800">
                        <input type="radio" name="airport-card-choice"
                          checked={airportCardSource==='new'}
                          onChange={()=>setAirportCardSource('new')} />
                        Pay with another card
                      </label>
                      {airportSavedCards.map(card=>(
                        <label key={card.saved_card_id}
                          className="flex items-center gap-2 text-xs font-medium text-gray-800">
                          <input type="radio" name="airport-card-choice"
                            checked={airportCardSource==='saved' && airportSelectedCardId===card.saved_card_id}
                            onChange={()=>{setAirportCardSource('saved');setAirportSelectedCardId(card.saved_card_id);}} />
                          {card.card_type || 'Card'} •••• {card.last4} ({card.bank || 'Bank card'})
                        </label>
                      ))}
                    </div>
                  )}

                {airportCardSource === 'new' && paymentMethod === 'card' &&
                  amountToPayNow > 0 &&
                  (selectedOption.type === 'private' || selectedOption.delay <= 5) && (
                    <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-2xl border border-gray-200 bg-white p-3">
                      <input
                        type="checkbox"
                        checked={saveCardForFuture}
                        disabled={loading}
                        onChange={(event) => setSaveCardForFuture(event.target.checked)}
                        className="mt-0.5 h-4 w-4 accent-[#720e1e]"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-gray-900">
                          Save my card for future UniAir payments
                        </span>
                        <span className="mt-1 block text-xs leading-5 text-gray-500">
                          Optional. Only a reusable Paystack card is saved after successful payment.
                          Use it for future rides and tips, or remove it under Profile → Payment methods.
                        </span>
                      </span>
                    </label>
                  )}

                {selectedOption.type === 'shared' && selectedOption.delay > 5 && paymentMethod === 'card' && (
                  <p className="mt-3 rounded-2xl border border-blue-200 bg-blue-50 px-3 py-3 text-xs leading-5 text-blue-800">
                    For shared requests, card payment happens after the passenger accepts. You will receive a Pay now to confirm seat prompt on your dashboard.
                  </p>
                )}

                {paymentMethod === 'card' &&
                  amountToPayNow > 0 &&
                  (selectedOption.type === 'private' || selectedOption.delay <= 5) && (
                    <div className="mt-3 rounded-2xl border border-[#720e1e]/15 bg-[#720e1e]/[0.035] px-3.5 py-3.5">
                      <div className="flex items-start gap-3">
                        <CreditCard className="mt-0.5 h-5 w-5 shrink-0 text-[#720e1e]" strokeWidth={1.9} />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Secure card payment</p>
                          <p className="mt-1 text-xs leading-5 text-gray-600">
                            Paystack will open securely and your bank may ask you to approve 3D Secure.
                            Keep the payment window open until you return to UniAir and see confirmation.
                          </p>
                          <p className="mt-2 text-xs leading-5 text-gray-500">
                            If the window closes before confirmation, wait a moment before retrying.
                            UniAir keeps this booking draft so you can retry payment without starting over.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                {paymentCloseNotice &&
                  paymentMethod === 'card' &&
                  amountToPayNow > 0 &&
                  (selectedOption.type === 'private' || selectedOption.delay <= 5) && (
                    <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs leading-5 text-amber-900">
                      <span className="font-semibold">Payment window closed.</span>{' '}
                      If you approved payment in your banking app, allow a moment for confirmation.
                      If you did not complete payment, use the button below to retry securely on this same booking.
                    </div>
                  )}

                {paymentMethod === 'cash' && (
                  <p className="mt-3 rounded-2xl border border-green-200 bg-green-50 px-3 py-3 text-xs leading-5 text-green-800">
                    Cash bookings are confirmed now. Pay the driver in cash on the day of your trip.
                  </p>
                )}
              </section>

              <section className="rounded-3xl border border-gray-200 bg-gray-50 p-4">
                <label htmlFor="terms-checkbox" className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-0.5 h-5 w-5 rounded border-gray-300 text-[#720e1e] focus:ring-[#720e1e]"
                  />
                  <span className="text-sm leading-6 text-gray-600">
                    I accept the{' '}
                    <Link to="/terms" className="font-semibold text-[#720e1e] underline underline-offset-2 hover:text-red-900">
                      Terms of Carriage
                    </Link>{' '}
                    and acknowledge that UniAir connects me with independent drivers using private vehicles.
                  </span>
                </label>
              </section>
            </div>

            <div className="sticky bottom-0 z-20 -mx-4 mt-5 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur-sm sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:pt-1">
              <button
                onClick={handleConfirmClick}
                disabled={loading}
                className="flex w-full items-center justify-center rounded-2xl bg-[#720e1e] px-4 py-4 text-base font-semibold text-white shadow-lg shadow-[#720e1e]/20 transition hover:bg-red-950 disabled:cursor-not-allowed disabled:bg-[#720e1e]/45 disabled:shadow-none"
              >
                {loading
                  ? paymentUiStage === 'confirming'
                    ? 'Confirming payment...'
                    : paymentUiStage === 'opening'
                    ? 'Opening secure payment...'
                    : paymentUiStage === 'preparing'
                    ? 'Preparing payment...'
                    : 'Processing...'
                  : selectedOption.type === 'shared' && selectedOption.delay > 5
                  ? 'Send join request'
                  : paymentMethod === 'card' && amountToPayNow > 0
                  ? paymentCloseNotice
                    ? 'Retry secure payment'
                    : 'Continue to secure payment'
                  : 'Confirm booking'}
              </button>
            </div>
          </section>
        )}
      {locationPickerOpen && (
        <UniAirLocationPicker
          brandLabel="UniAir"
          title={tripType === 'dropoff' ? 'Set pickup location' : 'Set destination'}
          searchHint={
            tripType === 'dropoff'
              ? `Search your address, residence, gate or landmark${zoneName ? ` in ${zoneName}` : ''}`
              : 'Search your destination address, residence, gate or landmark'
          }
          value={preciseLocation}
          areaMode={tripType === 'dropoff' ? 'all' : 'market'}
          searchScope={tripType === 'dropoff' ? 'all' : 'market'}
          onClose={() => setLocationPickerOpen(false)}
          onClear={() => handleLocationTextChange('')}
          onConfirm={(point) => {
            handleLocationSelected(point);
            setLocationPickerOpen(false);
          }}
        />
      )}
      </div>
    </div>
  );
}

function NotificationBanner({ notification }) {
  return (
    <div
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] px-6 py-3 rounded-xl shadow-lg flex items-center gap-3 w-11/12 md:w-auto justify-center ${
        notification.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
      }`}
    >
      <span className="text-xl">{notification.type === 'error' ? '⚠️' : '✓'}</span>
      <p className="font-medium text-sm">{notification.message}</p>
    </div>
  );
}

function InfoRow({ label, value, capitalize = false }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-gray-600">{label}</span>
      <span className={`font-bold text-right ${capitalize ? 'capitalize' : ''}`}>{value}</span>
    </div>
  );
}


function PromoPanel({
  draftBooking,
  appliedPromo,
  promoCode,
  setPromoCode,
  promoLoading,
  promoMsg,
  handleApplyPromo,
  clearDraftState,
}) {
  const appliedCode = draftBooking?.discount_code || appliedPromo?.promo_code || appliedPromo?.code;

  return (
    <section className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-center gap-2">
        <Ticket className="h-5 w-5 text-[#720e1e]" strokeWidth={1.9} />
        <p className="text-sm font-semibold text-gray-700">Promo code</p>
      </div>

      {appliedCode ? (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-green-200 bg-green-50 px-3 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-green-800">Applied: {appliedCode}</p>
            <p className="mt-1 text-xs text-green-700">
              Discount: {moneyText(draftBooking?.discount_amount ?? appliedPromo?.discount_amount)}
            </p>
          </div>
          <button
            type="button"
            onClick={() => clearDraftState()}
            className="shrink-0 text-xs font-semibold text-[#720e1e] underline underline-offset-2"
          >
            Remove
          </button>
        </div>
      ) : (
        <>
          <div className="mt-4 flex gap-2">
            <input
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              placeholder="e.g. NEWCOMER2026"
              className="min-w-0 flex-1 rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-medium text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-[#720e1e] focus:ring-2 focus:ring-[#720e1e]/15"
            />

            <button
              type="button"
              disabled={promoLoading}
              onClick={handleApplyPromo}
              className={`shrink-0 rounded-2xl px-5 py-3 text-sm font-semibold transition ${
                promoLoading
                  ? 'cursor-wait bg-[#720e1e]/45 text-white'
                  : 'bg-[#720e1e] text-white hover:bg-red-950'
              }`}
            >
              {promoLoading ? 'Applying...' : 'Apply'}
            </button>
          </div>

          {promoMsg && (
            <p className={`mt-3 text-xs font-semibold ${promoMsg.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
              {promoMsg.text}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function WalletPanel({ walletBalance, debtAmount, useWallet, setUseWallet, creditToApply }) {
  const hasCredit = walletBalance > 0;

  return (
    <section className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-2">
          <WalletCards className="h-5 w-5 shrink-0 text-gray-500" strokeWidth={1.9} />
          <p className="text-sm font-semibold text-gray-600">Wallet balance</p>
        </div>
        <p className={`shrink-0 text-base font-bold ${walletBalance >= 0 ? 'text-gray-900' : 'text-red-700'}`}>
          {walletBalance >= 0 ? moneyText(walletBalance) : `Debt: ${moneyText(debtAmount)}`}
        </p>
      </div>

      {hasCredit && (
        <label className="mt-4 flex cursor-pointer items-center justify-between gap-4 border-t border-gray-100 pt-4">
          <span>
            <span className="block text-sm font-semibold text-gray-800">Use wallet credit</span>
            <span className="mt-1 block text-xs text-green-700">Credit applied: {moneyText(creditToApply)}</span>
          </span>
          <input
            type="checkbox"
            checked={useWallet}
            onChange={(e) => setUseWallet(e.target.checked)}
            className="h-5 w-5 rounded border-gray-300 text-[#720e1e] focus:ring-[#720e1e]"
          />
        </label>
      )}
    </section>
  );
}

function NoOptionsCard({
  searchedTimeSummary,
  alternativeTimes,
  tripType,
  buildWhatsAppLink,
  retryWithSuggestedTime,
  onBack,
  onCheckAgain,
}) {
  return (
    <div className="bg-white rounded-2xl shadow p-6">
      <div className="text-center mb-6">
        <div className="text-5xl mb-3">😕</div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">No drivers available for this time right now</h3>
        <p className="text-sm text-gray-600 max-w-lg mx-auto">
          We could not find a driver for your selected time. Please try one of the available times below, check back
          later, or contact support and we’ll see if we can assist.
        </p>
      </div>

      {searchedTimeSummary && (
        <div className="mb-5 p-4 rounded-xl bg-gray-50 border">
          <p className="text-xs uppercase tracking-wide text-gray-500 font-bold mb-2">Your search</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <span className="text-gray-500">Date</span>
              <p className="font-bold text-gray-800">{searchedTimeSummary.date}</p>
            </div>

            <div>
              <span className="text-gray-500">Requested time</span>
              <p className="font-bold text-gray-800">{searchedTimeSummary.requestedTime}</p>
            </div>

            <div>
              <span className="text-gray-500">
                {tripType === 'pickup' ? 'Adjusted pickup time checked' : 'Time checked'}
              </span>
              <p className="font-bold text-gray-800">{searchedTimeSummary.actualSearchTime}</p>
            </div>
          </div>
        </div>
      )}

      {alternativeTimes.length > 0 ? (
        <div className="mb-6">
          <p className="text-sm font-bold text-gray-800 mb-3">Nearby available times</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {alternativeTimes.map((slot, idx) => (
              <button
                type="button"
                key={`${slot.slot_time}-${idx}`}
                onClick={() => retryWithSuggestedTime(slot.slot_time)}
                className="text-left border rounded-xl p-4 hover:border-maties-burgundy hover:bg-gray-50 transition"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-lg font-bold text-gray-800">{slot.slot_time}</p>
                    <p className="text-xs text-gray-500">
                      {slot.direction === 'earlier' ? `${slot.minutes_diff} mins earlier` : `${slot.minutes_diff} mins later`}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-sm font-bold text-green-700">
                      {slot.driver_count} driver{Number(slot.driver_count) === 1 ? '' : 's'}
                    </p>
                    <p className="text-xs text-maties-burgundy font-bold">Tap to search</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="mb-6 p-4 rounded-xl border border-yellow-200 bg-yellow-50">
          <p className="font-bold text-yellow-800 mb-1">No nearby available times found yet</p>
          <p className="text-sm text-yellow-700">Please come back later to check again — drivers may still add availability.</p>
        </div>
      )}

      <div className="border-t pt-5 space-y-3">
        <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
          <p className="font-bold text-blue-900 mb-1">Need help with this exact trip?</p>
          <p className="text-sm text-blue-800">Contact support and we can check with drivers directly.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a
            href={buildWhatsAppLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-[#720e1e] text-white py-4 rounded-xl font-bold flex items-center justify-center gap-3 shadow-lg shadow-red-100 hover:bg-red-950 transition-all active:scale-95 uppercase text-xs tracking-[0.08em]"
          >
            <WhatsAppIcon className="w-5 h-5 fill-white" />
            WhatsApp Support
          </a>

          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('UniAir booking help needed')}`}
            className="w-full inline-flex items-center justify-center gap-2 bg-white border border-gray-200 text-maties-burgundy py-4 px-4 rounded-xl font-bold hover:bg-gray-50 transition"
          >
            Email Support
          </a>
        </div>

        <p className="text-xs text-gray-500 text-center">WhatsApp: 067 665 6564 • Email: {SUPPORT_EMAIL}</p>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button onClick={onBack} className="w-full py-3 rounded-xl font-bold border text-gray-700 hover:bg-gray-50">
          Try a different time
        </button>

        <button onClick={onCheckAgain} className="w-full py-3 rounded-xl font-bold bg-maties-burgundy text-white hover:bg-red-900">
          Check again
        </button>
      </div>
    </div>
  );
}
